import './InternalMobile.css';
import React, { useEffect, useMemo, useState } from 'react';
import {
  FaCalendarAlt,
  FaBan,
  FaCheckCircle,
  FaChevronLeft,
  FaChevronRight,
  FaClipboardCheck,
  FaEdit,
  FaEnvelope,
  FaExclamationTriangle,
  FaFilter,
  FaHistory,
  FaLeaf,
  FaSearch,
  FaSyncAlt,
  FaTimes,
  FaUndo,
  FaUsers,
} from 'react-icons/fa';
import API_BASE_URL from './config/api';
import { JobberClientPicker } from './JobberBillingFields';
import {
  canSeeBillingDetails,
  canSeeCycleInvoicing,
  readMaintenanceUser,
} from './maintenanceAccess';
import { ScheduleMaintenanceModal } from './MaintenanceSchedule';
import { contractScopeLabel, contractScopeNeedsReview } from './utils/maintenanceScope';

const C = {
  navy: '#1F3A68',
  ink: '#172033',
  inkSoft: '#3f4b5f',
  muted: '#6b7280',
  line: '#E3E8F0',
  lineSoft: '#F0F3F8',
  bg: '#F4F7FB',
  card: '#ffffff',
  green: '#18864b',
  greenSoft: '#e9f7ef',
  blue: '#2563eb',
  blueSoft: '#eaf2ff',
  amber: '#a65b08',
  amberSoft: '#fff5dc',
  red: '#b4232c',
  redSoft: '#fff0f1',
  purple: '#6d42c7',
  purpleSoft: '#f1ecff',
  shadow: '0 1px 3px rgba(20,30,60,.05), 0 8px 24px rgba(20,30,60,.04)',
};

const sharedFont =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif';

const DAY_MS = 24 * 60 * 60 * 1000;
const SUPPORTED_CONTRACTS = new Set(['hvac-maintenance', 'dryer-vent']);

function parseLocalDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const match = String(value).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function toIso(date) {
  if (!date) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function addMonths(date, months) {
  const next = new Date(date);
  const day = next.getDate();
  next.setDate(1);
  next.setMonth(next.getMonth() + months);
  const finalDay = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
  next.setDate(Math.min(day, finalDay));
  return next;
}

const holidayCache = new Map();

function nthWeekday(year, month, weekday, occurrence) {
  const date = new Date(year, month, 1);
  date.setDate(1 + ((7 + weekday - date.getDay()) % 7) + ((occurrence - 1) * 7));
  return date;
}

function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = ((19 * a) + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + (2 * e) + (2 * i) - h - k) % 7;
  const m = Math.floor((a + (11 * h) + (22 * l)) / 451);
  const month = Math.floor((h + l - (7 * m) + 114) / 31) - 1;
  const day = ((h + l - (7 * m) + 114) % 31) + 1;
  return new Date(year, month, day);
}

function ontarioHolidayKeys(year) {
  if (holidayCache.has(year)) return holidayCache.get(year);
  const keys = new Set();
  const add = (date) => keys.add(toIso(date));
  const addFixedWithObservedDay = (month, day) => {
    const actual = new Date(year, month, day);
    add(actual);
    if (actual.getDay() === 6 || actual.getDay() === 0) {
      let observed = addDays(actual, actual.getDay() === 6 ? 2 : 1);
      while (observed.getDay() === 0 || observed.getDay() === 6 || keys.has(toIso(observed))) {
        observed = addDays(observed, 1);
      }
      add(observed);
    }
  };

  addFixedWithObservedDay(0, 1); // New Year's Day
  add(nthWeekday(year, 1, 1, 3)); // Family Day
  add(addDays(easterSunday(year), -2)); // Good Friday

  const victoriaDay = new Date(year, 4, 24);
  while (victoriaDay.getDay() !== 1) victoriaDay.setDate(victoriaDay.getDate() - 1);
  add(victoriaDay);

  addFixedWithObservedDay(6, 1); // Canada Day
  add(nthWeekday(year, 7, 1, 1)); // Civic Holiday
  add(nthWeekday(year, 8, 1, 1)); // Labour Day
  add(nthWeekday(year, 9, 1, 2)); // Thanksgiving
  const christmasHolidays = [new Date(year, 11, 25), new Date(year, 11, 26)];
  christmasHolidays.forEach(add);
  christmasHolidays.forEach((actual) => {
    if (actual.getDay() !== 0 && actual.getDay() !== 6) return;
    let observed = addDays(actual, actual.getDay() === 6 ? 2 : 1);
    while (observed.getDay() === 0 || observed.getDay() === 6 || keys.has(toIso(observed))) {
      observed = addDays(observed, 1);
    }
    add(observed);
  });

  holidayCache.set(year, keys);
  return keys;
}

function isBusinessDay(date) {
  const day = date.getDay();
  return day !== 0 && day !== 6 && !ontarioHolidayKeys(date.getFullYear()).has(toIso(date));
}

function nextBusinessDay(date) {
  let result = new Date(date);
  while (!isBusinessDay(result)) result = addDays(result, 1);
  return result;
}

function endAfterBusinessDays(start, totalWorkdays) {
  let result = nextBusinessDay(start);
  let remaining = Math.max(1, totalWorkdays) - 1;
  while (remaining > 0) {
    result = addDays(result, 1);
    if (isBusinessDay(result)) remaining -= 1;
  }
  return result;
}

function formatDate(date) {
  if (!date) return 'Dates need review';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatShortDate(date) {
  if (!date) return '—';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatWindow(start, end) {
  if (!start) return 'Dates need review';
  if (!end || toIso(start) === toIso(end)) return formatDate(start);
  if (start.getFullYear() === end.getFullYear()) {
    if (start.getMonth() === end.getMonth()) {
      return `${start.toLocaleDateString(undefined, { month: 'short' })} ${start.getDate()}–${end.getDate()}, ${end.getFullYear()}`;
    }
    return `${formatShortDate(start)}–${formatShortDate(end)}, ${end.getFullYear()}`;
  }
  return `${formatDate(start)}–${formatDate(end)}`;
}

function planningSeasonForDate(date, endDate) {
  if (!date) return null;
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  const endMonth = (endDate || date).getMonth() + 1;
  // Short January windows are leftover Fall work (Jan 4–15 stays Fall).
  // A January start that runs into March+ is the Spring block — 33 Bay's
  // dryer was Jan 22–Mar 2 and must stay Spring, not the previous Fall.
  if (month === 1) {
    if (endMonth >= 3) {
      return { name: 'Spring', year, key: seasonKey('Spring', year) };
    }
    return { name: 'Fall', year: year - 1, key: seasonKey('Fall', year - 1) };
  }
  if (month >= 2 && month <= 8) {
    return { name: 'Spring', year, key: seasonKey('Spring', year) };
  }
  return { name: 'Fall', year, key: seasonKey('Fall', year) };
}

function seasonForDate(date) {
  return planningSeasonForDate(date)?.name || null;
}

function seasonKey(name, year) {
  return `${name.toLowerCase()}-${year}`;
}

function seasonAnchor(name, year) {
  return new Date(year, name === 'Spring' ? 3 : 9, 1);
}

function seasonEnd(name, year) {
  return new Date(year, name === 'Spring' ? 7 : 11, name === 'Spring' ? 31 : 31);
}

// Billing window for a cycle. The two cycles partition the calendar
// exactly (Spring = Jan–Aug, Fall = Sep–Dec), which is what guarantees a
// maintenance invoice lands on one cycle and one cycle only — no invoice
// can be counted twice, and none can fall through the gap.
function seasonInvoiceWindow(season) {
  if (!season) return null;
  return season.name === 'Fall'
    ? { from: `${season.year}-09-01`, to: `${season.year}-12-31` }
    : { from: `${season.year}-01-01`, to: `${season.year}-08-31` };
}

// Matches the Division Margins formatting so the two pages read as one
// system when an L3 user compares them.
function formatMoney(value, decimals = 0) {
  return `$${Number(value || 0).toLocaleString('en-CA', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

export function buildPlanningSeasons(today = new Date(), count = 4) {
  const candidates = [];
  for (let year = today.getFullYear(); year <= today.getFullYear() + 3; year += 1) {
    ['Spring', 'Fall'].forEach((name) => {
      const end = seasonEnd(name, year);
      if (end >= today) {
        candidates.push({
          name,
          year,
          key: seasonKey(name, year),
          label: `${name} ${year}`,
          anchor: seasonAnchor(name, year),
        });
      }
    });
  }
  return candidates.slice(0, count);
}

// Finished cycles, oldest first. Display only: these are never passed as the
// `allSeasons` argument to the due-date logic, which treats allSeasons[0] as
// the current planning block and would mis-read a historical season there.
function buildHistorySeasons(today = new Date(), count = 6) {
  const finished = [];
  for (let year = today.getFullYear() - 4; year <= today.getFullYear(); year += 1) {
    ['Spring', 'Fall'].forEach((name) => {
      if (seasonEnd(name, year) < today) {
        finished.push({
          name,
          year,
          key: seasonKey(name, year),
          label: `${name} ${year}`,
          anchor: seasonAnchor(name, year),
          isPast: true,
        });
      }
    });
  }
  finished.sort((a, b) => seasonOrder(a) - seasonOrder(b));
  return finished.slice(-count);
}

function contractTypesOfHistory(item) {
  const types = [];
  const canonical = String(item?.Type || '').toLowerCase();
  if (canonical === 'dryer') types.push('dryer-vent');
  if (canonical === 'hvac') types.push('hvac-maintenance');
  const freeform = String(item?.maintenanceType || '').toLowerCase();
  if (freeform.includes('dryer')) types.push('dryer-vent');
  if (/(fan\s*coil|fancoil|heat\s*pump|heatpump|hvac|erv|humidifier)/i.test(freeform)) {
    types.push('hvac-maintenance');
  }
  return [...new Set(types)];
}

function contractTypeOfHistory(item) {
  const types = contractTypesOfHistory(item);
  if (types.length > 1) return 'combined';
  return types[0] || null;
}

function historyForContract(row) {
  const source = Array.isArray(row?.maintenanceHistory) ? row.maintenanceHistory : [];
  const seen = new Set();
  return source
    .filter((item) => {
      const inferred = contractTypesOfHistory(item);
      if (inferred.length && !inferred.includes(row.contractType)) return false;
      // Keep each exact inspection: a maintenance ID can be reused for
      // separate services or cycles. Filter by scope before deduplication.
      const id = item?.objectId || `${item?.maintenanceID}-${item?.startTerm}-${item?.endTerm}-${item?.Type}`;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    })
    .sort((a, b) => {
      const aDate = parseLocalDate(a?.endTerm || a?.startTerm);
      const bDate = parseLocalDate(b?.endTerm || b?.startTerm);
      return (bDate?.getTime() || 0) - (aDate?.getTime() || 0);
    });
}

function pastHistory(row) {
  return historyForContract(row).filter((item) => {
    const status = String(item?.status || '').toLowerCase();
    return status === 'past' || status === 'complete' || status === 'completed';
  });
}

function upcomingHistory(row) {
  const candidates = [];
  if (row?.upcomingInspection) candidates.push(row.upcomingInspection);
  historyForContract(row).forEach((item) => {
    const status = String(item?.status || '').toLowerCase();
    if (status === 'upcoming' || status === 'active') candidates.push(item);
  });
  const seen = new Set();
  return candidates.filter((item) => {
    const id = item?.objectId || `${item?.maintenanceID}-${item?.startTerm}-${item?.endTerm}`;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

function parsePlanningSeason(value, key = false) {
  const match = String(value || '').trim().match(key
    ? /^(spring|fall)-([12][0-9]{3})$/i
    : /^(spring|fall)(?:\s+|-)([12][0-9]{3})$/i);
  if (!match) return null;
  const name = match[1].toLowerCase() === 'spring' ? 'Spring' : 'Fall';
  const year = Number(match[2]);
  return { name, year, key: seasonKey(name, year) };
}

// Ownership follows the approved planning cycle, even when management moves
// its dates across the usual seasonal window. IDs here identify inspections,
// never the maintenance ID that older records sometimes reuse.
function inspectionSeasonOwners(planOverrides, buildingId) {
  const owners = new Map();
  Object.entries(planOverrides).forEach(([key, plan]) => {
    const planKey = String(plan?.planKey || key);
    if (!/^(spring|fall)-[12][0-9]{3}:[^:]+$/.test(planKey)) return;
    const [seasonPart, planBuildingId] = planKey.split(':');
    const season = parsePlanningSeason(seasonPart, true);
    if (planBuildingId !== buildingId || !season || !plan?.approvedInspectionId) return;
    const existing = owners.get(plan.approvedInspectionId) || [];
    if (!existing.some(owner => owner.key === season.key)) existing.push(season);
    owners.set(plan.approvedInspectionId, existing);
  });
  return owners;
}

function occurrenceSeason(item, owners = new Map()) {
  const linked = owners.get(item?.objectId) || [];
  if (linked.length === 1) return linked[0];
  // Inconsistent approval links must not silently unlock either booking.
  if (linked.length > 1) return { key: null, conflictingKeys: linked.map(owner => owner.key) };
  const planKey = String(item?.seasonalPlanKey || '').trim();
  const explicit = (/^(spring|fall)-[12][0-9]{3}:[^:]+$/.test(planKey)
    ? parsePlanningSeason(planKey.split(':')[0], true) : null)
    || parsePlanningSeason(item?.cycle);
  if (explicit) return explicit;
  const start = parseLocalDate(item?.startTerm || item?.startDate || item?.endTerm);
  const end = parseLocalDate(item?.endTerm || item?.endDate || item?.startTerm);
  return planningSeasonForDate(start, end);
}

function occurrenceBelongsToSeason(item, targetSeason, owners) {
  const assigned = occurrenceSeason(item, owners);
  return assigned?.key === targetSeason.key || assigned?.conflictingKeys?.includes(targetSeason.key);
}

function cyclesPerYear(row) {
  const value = Number(row?.cyclesPerYear ?? row?.frequency ?? 1);
  return Number.isFinite(value) && value > 0 ? value : 1;
}

function multiYearInterval(row) {
  const notes = String(contractRecord(row)?.notes || row?.contractNotes || '');
  const stated = notes.match(/(?:once|1\s*x)?\s*(?:every|per)\s+(\d+)\s+years?/i);
  if (stated) return Math.max(2, Number(stated[1]));
  const years = notes.match(/\b20\d{2}\b/g)?.map(Number).sort((a, b) => a - b) || [];
  if (years.length > 1) return Math.max(2, years[1] - years[0]);
  return Math.max(2, Math.round(1 / cyclesPerYear(row)));
}

function frequencyLabel(row) {
  const frequency = cyclesPerYear(row);
  if (frequency >= 3) return `${frequency}× per year`;
  if (frequency >= 1.5) return 'Spring + Fall';
  if (frequency === 1) return 'Once per year';
  return `Every ${multiYearInterval(row)} years`;
}

function contractTermLabel(row) {
  const months = Number(row?.termMonths || contractRecord(row)?.termLengthMonths || 0);
  if (!months) return 'Term not set';
  if (months % 12 === 0) {
    const years = months / 12;
    return `${years}-year term`;
  }
  return `${months}-month term`;
}

function nextDueDate(row) {
  const explicit = parseLocalDate(row?.nextDueDate);
  if (explicit) return explicit;
  const latest = pastHistory(row)[0];
  const last = parseLocalDate(latest?.endTerm || latest?.startTerm || row?.lastMaintenanceDate);
  if (!last) {
    const frequency = cyclesPerYear(row);
    if (frequency < 1) {
      const contract = contractRecord(row);
      const signed = parseLocalDate(contract?.signedDate || row?.signedDate);
      const serviceYears = String(contract?.notes || row?.contractNotes || '')
        .match(/\b20\d{2}\b/g)
        ?.map(Number)
        .filter((year, index, years) => years.indexOf(year) === index)
        .sort((a, b) => a - b) || [];
      if (serviceYears.length) {
        const year = serviceYears[0];
        return new Date(year, signed?.getMonth() || 0, signed?.getDate() || 1);
      }
      // A new multi-year contract is not automatically due in its signing
      // season. With no service-year note or history, its first renewal
      // interval is the earliest defensible due date.
      if (signed) return addMonths(signed, 12 * multiYearInterval(row));
    }
    return null;
  }
  const frequency = cyclesPerYear(row);
  return addMonths(last, frequency < 1 ? 12 * multiYearInterval(row) : Math.round(12 / frequency));
}

function preferredSeason(row, owners) {
  // Annual work follows the season in which that contract was actually
  // completed. This is more reliable than the legacy recommendation text,
  // which labels several Fall buildings as Spring.
  if (cyclesPerYear(row) <= 1) {
    const latest = pastHistory(row)[0];
    const historical = occurrenceSeason(latest, owners)?.name;
    if (historical) return historical;
  }
  const recommendation = String(row?.recommendedNextPeriod || '');
  if (/spring/i.test(recommendation)) return 'Spring';
  if (/fall/i.test(recommendation)) return 'Fall';
  const due = nextDueDate(row);
  if (due) return seasonForDate(due);
  const latest = pastHistory(row)[0];
  return seasonForDate(parseLocalDate(latest?.startTerm || latest?.endTerm)) || 'Spring';
}

function findScheduledOccurrence(row, targetSeason, owners) {
  return upcomingHistory(row).find((item) => occurrenceBelongsToSeason(item, targetSeason, owners)) || null;
}

function findCompletedOccurrence(row, targetSeason, owners) {
  return pastHistory(row).find((item) => occurrenceBelongsToSeason(item, targetSeason, owners)) || null;
}

function seasonOrder(season) {
  if (!season) return Number.POSITIVE_INFINITY;
  return (Number(season.year) * 2) + (season.name === 'Fall' ? 1 : 0);
}

function isMultiYearCycleDue(row, targetSeason, due, owners) {
  if (!due) return false;
  const interval = multiYearInterval(row);
  // Anchor the cadence to the season the last completed visit STARTED in.
  // The backend's due date is end-date based, so a Spring-block visit that
  // ran a couple of weeks into September would otherwise slip a whole
  // season (e.g. Aug 22–Sep 4, 2025 recurring in Fall 2027 instead of
  // Spring 2027).
  const lastDoneSeason = occurrenceSeason(pastHistory(row)[0], owners);
  const anchorName = lastDoneSeason?.name || occurrenceSeason({ startTerm: due })?.name;
  const anchorYear = lastDoneSeason ? lastDoneSeason.year + interval : due.getFullYear();
  if (!anchorName || targetSeason.name !== anchorName) return false;
  const yearsSince = targetSeason.year - anchorYear;
  return yearsSince >= 0 && yearsSince % interval === 0;
}

function contractRecord(row) {
  const contracts = Array.isArray(row?.contractData) ? row.contractData : [];
  return Number.isInteger(row?.contractIndex) ? (contracts[row.contractIndex] || {}) : {};
}

function contractExpiry(row) {
  return parseLocalDate(row?.expiryDate || contractRecord(row)?.expiryDate);
}

function isContractExpired(row) {
  const status = String(contractRecord(row)?.status || row?.contractStatus || '').toLowerCase();
  const expiry = contractExpiry(row);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return status === 'expired' || Boolean(expiry && expiry < today) || Number(row?.daysUntilExpiry) < 0;
}

// A missing history/due date means this work still needs a planning slot.
// Only a recorded season is a constraint; the display fallback of "Spring"
// must not make a new annual contract disappear from every planning block.
export function firstEligibleUnscheduledSeason(row, allSeasons, owners) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const signed = parseLocalDate(contractRecord(row)?.signedDate || row?.signedDate);
  const availableFrom = signed && signed > today ? signed : today;
  const completedSeasons = pastHistory(row).map(item => occurrenceSeason(item, owners)).filter(Boolean);
  const historicalSeason = cyclesPerYear(row) <= 1 ? completedSeasons[0]?.name : null;
  const recommendation = String(row?.recommendedNextPeriod || '');
  const allowedSeasons = historicalSeason
    ? [historicalSeason]
    : ['Spring', 'Fall'].filter((name) => new RegExp(name, 'i').test(recommendation));

  return [...allSeasons].sort((a, b) => seasonOrder(a) - seasonOrder(b)).find((season) => (
    seasonEnd(season.name, season.year) >= availableFrom
    && (!allowedSeasons.length || allowedSeasons.includes(season.name))
    && !(cyclesPerYear(row) < 1.5 && completedSeasons.some((done) => done.year === season.year))
  )) || null;
}

export function isDueInSeason(row, targetSeason, allSeasons, owners) {
  if (findScheduledOccurrence(row, targetSeason, owners)) return true;
  if (findCompletedOccurrence(row, targetSeason, owners)) return true;

  const frequency = cyclesPerYear(row);
  const completed = pastHistory(row);
  const firstScheduledSeason = upcomingHistory(row)
    .map(item => occurrenceSeason(item, owners))
    .filter(Boolean)
    .sort((a, b) => seasonOrder(a) - seasonOrder(b))[0];
  // A new contract begins with its first real scheduled visit. Do not create
  // ghost cycles in earlier seasons merely because its eventual cadence is
  // twice yearly (for example, a Fall start must not appear in Spring).
  if (
    completed.length === 0
    && firstScheduledSeason
    && seasonOrder(targetSeason) < seasonOrder(firstScheduledSeason)
  ) {
    return false;
  }
  const renewalContinuation = Boolean(
    row?.contractObligationComplete
    || Number(row?.contractCyclesRemaining) === 0
    || isContractExpired(row)
  );
  // If a first annual or multi-year visit is already assigned to another
  // season, do not also manufacture a "needs scheduling" card in the
  // default Spring block.
  if (frequency < 1.5 && firstScheduledSeason) return false;

  // Annual work already completed this planning year does not reopen in
  // the other season (33 Bay: Spring HVAC + dryer must not reappear in Fall).
  if (frequency < 1.5) {
    const doneThisYear = pastHistory(row).some((item) => occurrenceSeason(item, owners)?.year === targetSeason.year);
    if (
      doneThisYear
      && !findScheduledOccurrence(row, targetSeason, owners)
      && !findCompletedOccurrence(row, targetSeason, owners)
    ) {
      return false;
    }
  }

  // Fulfilled or expired contracts remain in the normal seasonal cadence as
  // renewal-continuation proposals. They are clearly flagged on the card,
  // but planners can still send dates while arranging the extension.
  if (renewalContinuation) {
    if (frequency >= 1.5) return true;
    const due = nextDueDate(row);
    if (!due) return targetSeason.key === firstEligibleUnscheduledSeason(row, allSeasons, owners)?.key;
    const preferred = preferredSeason(row, owners);
    if (frequency >= 1) return targetSeason.name === preferred;
    return isMultiYearCycleDue(row, targetSeason, due, owners);
  }

  if (frequency >= 1.5) return true;

  const due = nextDueDate(row);
  const preferred = preferredSeason(row, owners);
  if (!due) {
    return targetSeason.key === firstEligibleUnscheduledSeason(row, allSeasons, owners)?.key;
  }

  if (frequency < 1) {
    return isMultiYearCycleDue(row, targetSeason, due, owners);
  }
  if (due <= new Date()) {
    return targetSeason.key === allSeasons[0]?.key;
  }

  if (frequency >= 1) {
    return targetSeason.name === preferred && targetSeason.year >= due.getFullYear();
  }

  return false;
}

function historicalBenchmark(row, targetSeason, owners) {
  const histories = pastHistory(row);
  const benchmarkYear = targetSeason.year - 1;
  const priorYearSeason = histories.find((item) => {
    const owner = occurrenceSeason(item, owners);
    return owner?.name === targetSeason.name && owner?.year === benchmarkYear;
  });
  const source = priorYearSeason || null;
  if (!source) {
    return {
      start: null,
      end: null,
      source: null,
      confidence: 'needs-review',
      explanation: `No ${targetSeason.name} ${benchmarkYear} cycle to benchmark`,
    };
  }

  const oldStart = parseLocalDate(source.startTerm || source.startDate || source.endTerm);
  const oldEnd = parseLocalDate(source.endTerm || source.endDate || source.startTerm);
  if (!oldStart) {
    return {
      start: null,
      end: null,
      source,
      confidence: 'needs-review',
      explanation: 'Previous cycle is missing dates',
    };
  }

  // Preserve the historical inclusive duration as intended workdays, then
  // lay those workdays onto the new calendar without consuming weekends or
  // Ontario holidays. A seven-day job beginning Monday therefore ends the
  // following Tuesday rather than Sunday.
  const totalWorkdays = Math.max(1, Math.round(((oldEnd || oldStart) - oldStart) / DAY_MS) + 1);
  const proposedYear = oldStart.getFullYear() + (targetSeason.year - benchmarkYear);
  const maxDay = new Date(proposedYear, oldStart.getMonth() + 1, 0).getDate();
  const proposedStart = nextBusinessDay(
    new Date(proposedYear, oldStart.getMonth(), Math.min(oldStart.getDate(), maxDay)),
  );
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const originalProposalWasPast = proposedStart < today;
  if (
    originalProposalWasPast
    && targetSeason.year === today.getFullYear()
    && targetSeason.name === seasonForDate(today)
  ) {
    // Do not collapse every overdue proposal onto the same arbitrary
    // future day. The honest state is "date required" until a planner
    // deliberately adjusts it.
    return {
      start: null,
      end: null,
      source,
      confidence: 'needs-review',
      explanation: `${targetSeason.name} ${benchmarkYear} dates have already passed`,
    };
  }
  return {
    start: proposedStart,
    end: endAfterBusinessDays(proposedStart, totalWorkdays),
    source,
    confidence: 'history',
    explanation: `Benchmarked from ${targetSeason.name} ${benchmarkYear}`,
  };
}

function contractNotes(row) {
  const item = contractRecord(row);
  return String(item?.notes || row?.contractNotes || '').trim();
}

function workingDaysBetween(start, end) {
  if (!start) return 0;
  const last = end || start;
  let count = 0;
  for (let cursor = new Date(start); cursor <= last; cursor = addDays(cursor, 1)) {
    if (isBusinessDay(cursor)) count += 1;
  }
  return count;
}

function dryerScopeNeedsReview(row) {
  if (!row || row.contractType !== 'dryer-vent') return false;
  return contractScopeNeedsReview(row);
}

function equipmentLabel(row) {
  const device = Array.isArray(row?.deviceType) ? row.deviceType.join(' ') : String(row?.deviceType || '');
  const history = (Array.isArray(row?.maintenanceHistory) ? row.maintenanceHistory : [])
    .map((item) => `${item?.maintenanceType || ''} ${item?.deviceType || ''}`)
    .join(' ');
  const raw = `${device} ${history}`;
  if (/heat\s*pump|heatpump/i.test(raw)) return 'Heat pump';
  if (/fan\s*coil|fancoil/i.test(raw)) return 'Fan coil';
  return 'HVAC maintenance';
}

function filterSummary(rows) {
  const row = rows.find((item) => item?.filterSize || item?.filterQuantity || item?.filterType);
  if (!row) return null;
  const parts = [
    row.filterQuantity ? `${row.filterQuantity} filters` : null,
    row.filterSize || null,
    row.filterType || null,
  ].filter(Boolean);
  return parts.join(' · ') || null;
}

function formatFilterDetails(type, size, quantity) {
  return [
    quantity ? `${quantity} per unit` : null,
    size || null,
    type || null,
  ].filter(Boolean).join(' · ') || null;
}

function dedupeMaintenanceHistory(items) {
  const cycles = new Map();
  items.forEach((item) => {
    const key = item?.maintenanceID
      ? `${item.maintenanceID}:${item.cycle || ''}:${item.startTerm || item.startDate || ''}:${item.endTerm || item.endDate || ''}`
      : item?.objectId || `${item?.startTerm}-${item?.endTerm}-${item?.maintenanceType}`;
    const existing = cycles.get(key);
    if (!existing) {
      cycles.set(key, item);
      return;
    }
    // Some older combined visits were saved as two inspection objects with
    // the same maintenance ID (one HVAC and one dryer). Preserve both scope
    // labels while showing the visit only once.
    const scopeTypes = new Set([
      ...contractTypesOfHistory(existing),
      ...contractTypesOfHistory(item),
    ]);
    const scopes = [
      existing?.maintenanceType,
      item?.maintenanceType,
      scopeTypes.has('hvac-maintenance') ? 'HVAC' : null,
      scopeTypes.has('dryer-vent') ? 'Dryer vent' : null,
    ].filter(Boolean);
    cycles.set(key, {
      ...existing,
      maintenanceType: [...new Set(scopes)].join(', '),
    });
  });
  return [...cycles.values()];
}

function nextMaintenanceId(card) {
  const prefix = `${card.buildingId}-`;
  const highest = (card.history || []).reduce((max, item) => {
    const raw = String(item?.maintenanceID || '');
    if (!raw.startsWith(prefix)) return max;
    const sequence = Number(raw.slice(prefix.length));
    return Number.isFinite(sequence) ? Math.max(max, sequence) : max;
  }, 0);
  return `${card.buildingId}-${highest + 1}`;
}

export function maintenanceIdForPlan(card, override, managementApproved, inspection) {
  if (managementApproved) return inspection?.maintenanceID || override.maintenanceId || nextMaintenanceId(card);
  const proposed = override.maintenanceId || '';
  const partial = card.partiallyScheduled || card.partiallyCompleted;
  // A remaining service is a new inspection. Do not prefill the booked
  // service's existing ID when its original combined plan is still open.
  if (partial && card.history.some(item => item.maintenanceID === proposed)) return nextMaintenanceId(card);
  return proposed || nextMaintenanceId(card);
}

export function maintenanceTypesForCard(card) {
  const types = new Set();
  const lockedTypes = card.lockedContractTypes || [];
  const hvac = !lockedTypes.includes('hvac-maintenance') && card.hvac;
  const dryer = !lockedTypes.includes('dryer-vent') && card.dryer;
  // Device types describe the whole building, including on a dryer row.
  // Only an included HVAC contract may add HVAC forms to this visit.
  const raw = (hvac ? [hvac] : [])
    .flatMap((row) => Array.isArray(row?.deviceType) ? row.deviceType : [row?.deviceType])
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (/fan[\s-]?coil|fancoil/.test(raw)) types.add('fancoil');
  if (/heat[\s-]?pump|heatpump/.test(raw)) types.add('heatpump');
  if (/\berv\b/.test(raw)) types.add('erv');
  if (/humidifier/.test(raw)) types.add('humidifier');
  if (hvac && types.size === 0) types.add('fancoil');
  if (dryer) types.add('dryer vent');
  return [...types];
}

export function assertSeasonalScopeSelection(card, inspectionData) {
  if (!card || card.status === 'skipped' || card.allScopesSkipped) {
    throw new Error('This seasonal visit is skipped. Include a service in Adjust before scheduling it.');
  }
  const requestedTypes = contractTypesOfHistory(inspectionData);
  const unavailable = new Set([
    ...(card.skippedScopes || []).map(item => item.row.contractType),
    ...(card.lockedContractTypes || []),
  ]);
  if (requestedTypes.some(type => unavailable.has(type))) {
    throw new Error('This selection includes a skipped or already booked/completed service. Return to Adjust to review this season’s scope.');
  }
}

// Creation and plan approval are separate writes. Never let a failed or
// ambiguous plan link reach the modal's Jobber booking step. The server also
// defers its automatic Jobber sync for requests carrying seasonalPlanKey.
export async function approveSeasonalSchedule({ card, season, inspectionData, savePlan, onRecoveredPlan, retainedJobId = '', fetcher = fetch }) {
  assertSeasonalScopeSelection(card, inspectionData);
  const planKey = `${season.key}:${card.buildingId}`;
  const response = await fetcher(`${API_BASE_URL}/api/maintenance-inspections`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...inspectionData,
      seasonalPlanKey: planKey,
      expectedSeasonalPlanUpdatedAt: card.planUpdatedAt || '',
    }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body?.error || 'Failed to approve schedule.');
  const inspectionId = body.objectId || '';
  if (!inspectionId) throw new Error('The saved schedule could not be identified. Refresh before approving again.');

  try {
    const linked = await savePlan({ approvedInspectionId: inspectionId, approvedAt: new Date().toISOString() });
    if (!linked) throw new Error('The seasonal approval link could not be saved.');
  } catch (linkError) {
    // A lost response can follow a successful save. Reconcile the exact link
    // and selected scope; do not delete an inspection that may be approved or
    // an existing record returned by creation's duplicate protection.
    let recovered = null;
    try {
      const check = await fetcher(`${API_BASE_URL}/api/maintenance-seasonal-plans`, { cache: 'no-store' });
      const current = await check.json();
      if (check.ok && Array.isArray(current.plans)) {
        const plan = current.plans.find(item => item.planKey === planKey);
        const requested = contractTypesOfHistory(inspectionData);
        if (plan?.approvedInspectionId === inspectionId && !plan.skippedThisCycle
          && requested.every(type => !plan.scopeDecisions?.[type]?.skipped)) recovered = plan;
      }
    } catch (_) { /* An uncertain result must remain unbooked for review. */ }
    if (!recovered) {
      throw new Error(`Maintenance ${inspectionId} was saved, but its seasonal approval could not be confirmed. No Jobber booking was requested. Refresh and review this saved maintenance before trying again. ${linkError.message || ''}`.trim());
    }
    if (onRecoveredPlan) onRecoveredPlan(recovered);
  }

  // Updating an inspection also triggers server-side Jobber sync, so reassociate
  // a retained job only AFTER the plan link is confirmed. Keep its recovery
  // reference on the plan if this step cannot be verified.
  if (retainedJobId) {
    try {
      const restored = await fetcher(`${API_BASE_URL}/api/maintenance-inspections/${encodeURIComponent(inspectionId)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jobberJobId: retainedJobId }),
      });
      if (!restored.ok) throw new Error('Jobber reference could not be saved.');
    } catch (_) {
      throw new Error(`Maintenance ${inspectionId} is approved, but its existing Jobber job link could not be confirmed. Refresh and review it before trying again.`);
    }
  }
  return inspectionId;
}

export function seasonalInspectionTarget(card, plan = {}) {
  const hasCurrentApproval = Object.prototype.hasOwnProperty.call(plan, 'approvedInspectionId');
  if (hasCurrentApproval && card.approvedInspectionId && plan.approvedInspectionId !== card.approvedInspectionId) {
    throw new Error('This seasonal approval changed. Refresh before editing its schedule.');
  }
  const approvedId = hasCurrentApproval ? plan.approvedInspectionId : card.approvedInspectionId;
  if (approvedId) {
    const inspection = [card.approvedInspection, ...(card.history || []), card.scheduledPrimary, card.completedPrimary]
      .find(item => item?.objectId === approvedId);
    if (!inspection) throw new Error('The approved maintenance record is unavailable. Refresh or review it in Schedule before making changes.');
    return inspection;
  }
  return card.scheduledPrimary || card.completedPrimary || null;
}

export async function updateSeasonalInspection(card, plan, patch, fetcher = fetch) {
  const owned = ['start', 'end', 'dailySchedule', 'pricingDetails', 'maintenanceId'];
  if (!owned.some(field => field in patch)) return null;
  const inspection = seasonalInspectionTarget(card, plan);
  if (!inspection?.objectId) throw new Error('Could not find the approved schedule record for this building.');
  const payload = {};
  if (patch.start) { payload.startTerm = patch.start; payload.startDate = patch.start; }
  if (patch.end) { payload.endTerm = patch.end; payload.endDate = patch.end; }
  if ('dailySchedule' in patch) payload.dailySchedule = patch.dailySchedule || '';
  if ('pricingDetails' in patch) payload.pricingDetails = patch.pricingDetails || '';
  if (patch.maintenanceId) payload.maintenanceID = patch.maintenanceId;
  const response = await fetcher(`${API_BASE_URL}/api/maintenance-inspections/${encodeURIComponent(inspection.objectId)}`, {
    method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.error || `Could not update the approved schedule (${response.status}).`);
  }
  return inspection;
}

export async function releaseSeasonalInspection(card, plan, fetcher = fetch) {
  const inspection = seasonalInspectionTarget(card, plan);
  if (!inspection?.objectId) throw new Error('Could not find the approved schedule record for this building.');
  const status = String(inspection.status || '').toLowerCase();
  const start = parseLocalDate(inspection.startTerm || inspection.startDate);
  if (status !== 'upcoming' || (start && toIso(start) < toIso(new Date()))) {
    throw new Error('Work that is active, completed, or already past its start date cannot be removed from the planner. Review this maintenance in Schedule.');
  }
  const targetUrl = `${API_BASE_URL}/api/maintenance-inspections/${encodeURIComponent(inspection.objectId)}`;
  const unschedule = await fetcher(`${targetUrl}/jobber-unschedule`, { method: 'POST' });
  const released = await unschedule.json().catch(() => ({}));
  if (!unschedule.ok || !released.ok) throw new Error(released.error || 'Jobber would not release the visit.');
  const removed = await fetcher(targetUrl, { method: 'DELETE' });
  if (!removed.ok) {
    const body = await removed.json().catch(() => ({}));
    throw new Error(body?.error || 'Could not remove the approved schedule.');
  }
  return { inspection, jobId: released.jobId || '' };
}

function approvedActionDescription(card) {
  const inspection = card.approvedInspection || (!card.approvedInspectionId && (card.scheduledPrimary || card.completedPrimary));
  if (!inspection) return card.approvedInspectionId ? 'Approved maintenance record unavailable — refresh before changing it.' : '';
  return `${inspection.maintenanceType || inspection.Type || 'Maintenance'} · ${inspection.maintenanceID || inspection.objectId}`;
}

function emailScopeLabel(card) {
  const scopes = [];
  if (card.hvac) scopes.push(equipmentLabel(card.hvac).replace(/\s+maintenance$/i, '').toLowerCase());
  if (card.dryer) scopes.push(contractScopeLabel(card.dryer).toLowerCase());
  if (!scopes.length) return 'seasonal maintenance';
  if (scopes.length === 1) return `${scopes[0]} maintenance`;
  return `${scopes.slice(0, -1).join(', ')} and ${scopes[scopes.length - 1]} maintenance`;
}

function managementEmailCopy(card, season) {
  const building = card.address || card.name;
  const scope = emailScopeLabel(card);
  const dates = formatWindow(card.start, card.end);
  return [
    `Subject: Proposed ${season.label} Maintenance Dates — ${card.name}`,
    '',
    `Hello ${card.name} Management,`,
    '',
    `We have tentatively scheduled the ${scope} at ${building} for ${dates}. Please let us know if these dates work for the building.`,
    '',
    'Once confirmed, we will lock in the schedule and provide a floor-by-floor breakdown notice for posting to residents.',
    '',
    'Thank you,',
    'Hudson Condominium Solutions',
  ].join('\n');
}

async function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch (error) {
      // Some managed browsers deny the Clipboard API even on HTTPS. Fall
      // through to the selection-based copy method in that case.
    }
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  document.body.removeChild(textarea);
  if (!copied) throw new Error('Clipboard access was denied.');
}

export function buildSeasonCards(rows, targetSeason, allSeasons, planOverrides = {}) {
  const grouped = new Map();
  rows.forEach((row) => {
    if (!SUPPORTED_CONTRACTS.has(row?.contractType)) return;
    const key = String(row.buildingId || row.objectId || row.name);
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(row);
  });

  const cards = [];
  grouped.forEach((buildingRows, buildingId) => {
    const plan = planOverrides[`${targetSeason.key}:${buildingId}`] || {};
    const owners = inspectionSeasonOwners(planOverrides, buildingId);
    const scopeDecisions = plan.scopeDecisions || {};
    const hasScopeDecision = type => typeof scopeDecisions[type]?.skipped === 'boolean';
    const hvac = buildingRows.find((row) => row.contractType === 'hvac-maintenance');
    const dryer = buildingRows.find((row) => row.contractType === 'dryer-vent');
    // Keep an explicitly reviewed scope in its own season even after the
    // next due date moves forward. Restoring a skipped service must not make
    // it silently disappear; an existing booking remains authoritative.
    const hvacDue = hvac && (isDueInSeason(hvac, targetSeason, allSeasons, owners)
      || hasScopeDecision('hvac-maintenance'));
    let dryerDue = dryer && (isDueInSeason(dryer, targetSeason, allSeasons, owners)
      || hasScopeDecision('dryer-vent'));

    // Buildings generally prefer dryer work alongside HVAC. If dryer is due
    // within roughly one seasonal block of this HVAC occurrence, attach it.
    let dryerAligned = false;
    if (hvacDue && dryer && !dryerDue) {
      const due = nextDueDate(dryer);
      const distance = due ? Math.abs(due - targetSeason.anchor) / DAY_MS : Infinity;
      if (distance <= 120) {
        dryerDue = true;
        dryerAligned = true;
      }
    }

    // Dryer already placed in a different season need not be proposed here.
    // Completed HVAC alone is not evidence that management skipped dryer.
    const dryerHere = Boolean(
      dryer
      && (findScheduledOccurrence(dryer, targetSeason, owners) || findCompletedOccurrence(dryer, targetSeason, owners))
    );
    const dryerPlacedElsewhere = Boolean(dryer && !dryerHere && (
      upcomingHistory(dryer).some((item) => {
        const occ = occurrenceSeason(item, owners);
        return occ && occ.key !== targetSeason.key;
      })
      || pastHistory(dryer).some((item) => {
        const occ = occurrenceSeason(item, owners);
        return occ && occ.key !== targetSeason.key && occ.year === targetSeason.year;
      })
    ));
    if (dryerDue && !dryerHere && dryerPlacedElsewhere
      && !hasScopeDecision('dryer-vent')) {
      dryerDue = false;
      dryerAligned = false;
    }

    if (!hvacDue && !dryerDue) return;
    const plannedRows = [hvacDue ? hvac : null, dryerDue ? dryer : null].filter(Boolean);
    const approvedInspection = plan.approvedInspectionId
      ? buildingRows.flatMap(row => [...(row.maintenanceHistory || []), row.upcomingInspection].filter(Boolean))
        .find(item => item.objectId === plan.approvedInspectionId)
      : null;
    const lockedContractTypes = plannedRows.filter(row => (
      findScheduledOccurrence(row, targetSeason, owners) || findCompletedOccurrence(row, targetSeason, owners)
      || (plan.approvedInspectionId && (!approvedInspection
        || contractTypesOfHistory(approvedInspection).includes(row.contractType)))
    )).map(row => row.contractType);
    const skippedScopes = plannedRows.filter(row => (
      scopeDecisions[row.contractType]?.skipped === true
      && !lockedContractTypes.includes(row.contractType)
    )).map(row => ({ row, reason: String(scopeDecisions[row.contractType]?.reason || '') }));
    const includedRows = plannedRows.filter(row => !skippedScopes.some(item => item.row === row));
    const allScopesSkipped = includedRows.length === 0;
    const scheduledItems = includedRows
      .map((row) => ({ row, inspection: findScheduledOccurrence(row, targetSeason, owners) }))
      .filter((item) => item.inspection);
    const completedItems = includedRows
      .map((row) => ({ row, inspection: findCompletedOccurrence(row, targetSeason, owners) }))
      .filter((item) => item.inspection);

    const primary = includedRows[0] || plannedRows[0];
    const approvedStatus = String(approvedInspection?.status || '').toLowerCase();
    const scheduledPrimary = plan.approvedInspectionId
      ? (['upcoming', 'active'].includes(approvedStatus) ? approvedInspection : undefined)
      : scheduledItems.find((item) => item.row === primary)?.inspection || scheduledItems[0]?.inspection;
    const completedPrimary = plan.approvedInspectionId
      ? (['past', 'complete', 'completed'].includes(approvedStatus) ? approvedInspection : undefined)
      : completedItems.find((item) => item.row === primary)?.inspection || completedItems[0]?.inspection;
    const benchmark = historicalBenchmark(primary, targetSeason, owners);
    const fullyCompleted = !allScopesSkipped && completedItems.length === includedRows.length;
    // A combined visit is fully covered when every included scope is either
    // completed or still active/upcoming. The two scopes do not need to have
    // identical lifecycle statuses while technicians finish closing records.
    const fullyCovered = !allScopesSkipped && includedRows.every((row) => (
      scheduledItems.some((item) => item.row === row)
      || completedItems.some((item) => item.row === row)
    ));
    const partiallyScheduled = scheduledItems.length > 0 && !fullyCovered;
    const partiallyCompleted = completedItems.length > 0 && !fullyCovered;
    const start = approvedInspection
      ? parseLocalDate(approvedInspection.startTerm || approvedInspection.startDate)
      : plan.approvedInspectionId ? parseLocalDate(plan.start)
      : scheduledPrimary
      ? parseLocalDate(scheduledPrimary.startTerm || scheduledPrimary.startDate)
      : fullyCompleted && completedPrimary
        ? parseLocalDate(completedPrimary.startTerm || completedPrimary.startDate)
        : benchmark.start;
    const end = approvedInspection
      ? parseLocalDate(approvedInspection.endTerm || approvedInspection.endDate || approvedInspection.startTerm)
      : plan.approvedInspectionId ? parseLocalDate(plan.end)
      : scheduledPrimary
      ? parseLocalDate(scheduledPrimary.endTerm || scheduledPrimary.endDate || scheduledPrimary.startTerm)
      : fullyCompleted && completedPrimary
        ? parseLocalDate(completedPrimary.endTerm || completedPrimary.endDate || completedPrimary.startTerm)
        : benchmark.end;
    const needsScopeReview = includedRows.some(dryerScopeNeedsReview);
    const expiredContract = includedRows.some(isContractExpired);
    const renewalRequired = includedRows.some((row) => (
      row?.contractObligationComplete
      || Number(row?.contractCyclesRemaining) === 0
      || isContractExpired(row)
    ));
    const expiries = includedRows.map(contractExpiry).filter(Boolean);
    const earliestExpiry = expiries.sort((a, b) => a - b)[0] || null;
    const expiresBeforeVisit = Boolean(!expiredContract && earliestExpiry && start && earliestExpiry < start);
    const expiringAfterMaintenance = !expiredContract && !expiresBeforeVisit && (
      includedRows.some((row) => row?.isExpiringSoon)
      || Boolean(earliestExpiry && end && earliestExpiry <= addMonths(end, 3))
    );
    const extraCyclesRequired = includedRows.some((row) => cyclesPerYear(row) > 2);
    // Keep the workflow intentionally simple. Contract/date concerns are
    // shown as alerts on the card, not as extra workflow statuses.
    const status = allScopesSkipped ? 'skipped' : fullyCovered ? 'scheduled' : 'planning';
    const notes = [...new Set(includedRows.map(contractNotes).filter(Boolean))].join(' • ');
    const latestPast = includedRows
      .flatMap(pastHistory)
      .sort((a, b) => {
        const ad = parseLocalDate(a?.endTerm || a?.startTerm);
        const bd = parseLocalDate(b?.endTerm || b?.startTerm);
        return (bd?.getTime() || 0) - (ad?.getTime() || 0);
      })[0];

    cards.push({
      buildingId,
      objectId: primary?.objectId,
      name: primary?.name || primary?.building_name || 'Unnamed building',
      address: primary?.address || '',
      units: Number(primary?.units || 0),
      rows: includedRows,
      plannedRows,
      scopeDecisions,
      skippedScopes,
      lockedContractTypes,
      allScopesSkipped,
      approvedInspectionId: plan.approvedInspectionId || '',
      approvedInspection,
      approvedPlanCoversAll: !allScopesSkipped && Boolean(plan.approvedInspectionId)
        && includedRows.every(row => !approvedInspection
          || contractTypesOfHistory(approvedInspection).includes(row.contractType)),
      hvac: includedRows.find(row => row.contractType === 'hvac-maintenance') || null,
      dryer: includedRows.find(row => row.contractType === 'dryer-vent') || null,
      dryerAligned,
      status,
      start,
      end,
      benchmark,
      scheduledPrimary,
      completedPrimary,
      scheduledCount: scheduledItems.length,
      scopeApprovals: includedRows.map(row => {
        const completed = completedItems.find(item => item.row === row)?.inspection;
        const scheduled = scheduledItems.find(item => item.row === row)?.inspection;
        return { row, inspection: completed || scheduled || null, status: completed ? 'completed' : scheduled ? 'approved' : 'pending' };
      }),
      partiallyScheduled,
      partiallyCompleted,
      needsScopeReview,
      expiredContract,
      renewalRequired,
      expiresBeforeVisit,
      expiringAfterMaintenance,
      earliestExpiry,
      extraCyclesRequired,
      filter: filterSummary(includedRows),
      filterType: primary?.filterType || '',
      filterSize: primary?.filterSize || '',
      filterQuantity: primary?.filterQuantity || '',
      jobberClientId: primary?.jobberClientId || '',
      jobberPropertyId: primary?.jobberPropertyId || '',
      jobberClientName: primary?.jobberClientName || '',
      notes,
      latestPast,
      // History is building-wide across every supported contract, even when
      // only one scope is due in the selected season. Otherwise the same
      // completed visit can misleadingly lose its dryer or HVAC scope when
      // viewed from a different seasonal tab.
      history: dedupeMaintenanceHistory(buildingRows.flatMap(historyForContract))
        .sort((a, b) => {
          const ad = parseLocalDate(a?.endTerm || a?.startTerm);
          const bd = parseLocalDate(b?.endTerm || b?.startTerm);
          return (bd?.getTime() || 0) - (ad?.getTime() || 0);
        }),
    });
  });

  return cards.sort((a, b) => {
    const dateDiff = (a.start?.getTime() || Infinity) - (b.start?.getTime() || Infinity);
    return dateDiff || a.name.localeCompare(b.name);
  });
}

function statusMeta(status) {
  if (status === 'scheduled') {
    return { label: 'Management Approved', bg: C.greenSoft, fg: C.green, icon: <FaCheckCircle /> };
  }
  if (status === 'partial') {
    return { label: 'Partly approved', bg: C.amberSoft, fg: C.amber, icon: <FaClipboardCheck /> };
  }
  if (status === 'sent') {
    return { label: 'Dates sent', bg: C.amberSoft, fg: C.amber, icon: <FaClipboardCheck /> };
  }
  if (status === 'skipped') {
    return { label: 'Skipped this cycle', bg: C.lineSoft, fg: C.muted, icon: <FaBan /> };
  }
  return { label: 'Needs Scheduling', bg: C.redSoft, fg: C.red, icon: <FaLeaf /> };
}

// Pulls the cycle's maintenance invoices (L3 only). The backend crawl is
// slow enough to run on a background warm, so a cold cycle answers
// `warming` and we poll until it lands rather than blocking the planner.
function useCycleInvoices(season, enabled, refreshToken) {
  const range = useMemo(() => seasonInvoiceWindow(season), [season]);
  const rangeKey = range ? `${range.from}::${range.to}` : null;
  const [state, setState] = useState({ status: 'idle', key: null, byClient: {}, summary: null });

  useEffect(() => {
    if (!enabled || !range) {
      setState({ status: 'idle', key: null, byClient: {}, summary: null });
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    let polls = 0;
    const MAX_POLLS = 40; // ~3.5 min at 5s — a cold cycle takes ~1-2 min

    // Switching cycles must drop the previous cycle's figures instantly —
    // showing Spring's dollars against Fall's cards for even one frame is
    // worse than showing nothing. A plain refresh keeps them on screen.
    setState((current) => (current.key === rangeKey
      ? { ...current, status: 'revalidating' }
      : { status: 'loading', key: rangeKey, byClient: {}, summary: null }));

    const tick = async (isInitial) => {
      try {
        const token = localStorage.getItem('managerToken') || '';
        const qs = isInitial && refreshToken > 0 ? '&fresh=1' : '';
        const response = await fetch(
          `${API_BASE_URL}/api/jobber/maintenance-invoices?from=${range.from}&to=${range.to}${qs}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} },
        );
        const body = await response.json().catch(() => ({}));
        if (cancelled) return;

        if (response.status === 403) {
          setState({ status: 'forbidden', key: rangeKey, byClient: {}, summary: null });
          return;
        }
        if (response.ok && body?.ok) {
          setState({
            status: body._revalidating ? 'revalidating' : 'ready',
            key: rangeKey,
            byClient: body.byClient || {},
            summary: body.summary || null,
          });
          if (body._revalidating && polls < MAX_POLLS) {
            polls += 1;
            timer = window.setTimeout(() => tick(false), 5000);
          }
          return;
        }
        if (body?.reason === 'warming' && polls < MAX_POLLS) {
          polls += 1;
          setState((current) => ({ ...current, status: 'loading' }));
          timer = window.setTimeout(() => tick(false), 5000);
          return;
        }
        setState((current) => ({
          ...current,
          status: current.summary ? 'ready' : 'error',
        }));
      } catch (err) {
        if (!cancelled) setState((current) => ({ ...current, status: current.summary ? 'ready' : 'error' }));
      }
    };

    tick(true);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, rangeKey, refreshToken]);

  return state;
}

export default function MaintenanceSeasonalPlanning() {
  const seasons = useMemo(() => buildPlanningSeasons(), []);
  const history = useMemo(() => buildHistorySeasons(), []);
  // Full strip the arrows page through: finished cycles then the planning
  // window. `seasons` stays the planning window on its own, because the
  // due-date logic reads allSeasons[0] as "the current block".
  const timeline = useMemo(() => [...history, ...seasons], [history, seasons]);
  const VISIBLE_SEASONS = 4;
  const maxWindowStart = Math.max(0, timeline.length - VISIBLE_SEASONS);
  // Opens on the planning window, so the default view is unchanged.
  const [windowStart, setWindowStart] = useState(() =>
    Math.min(history.length, Math.max(0, timeline.length - VISIBLE_SEASONS)));
  const visibleSeasons = timeline.slice(windowStart, windowStart + VISIBLE_SEASONS);
  const [selectedSeasonKey, setSelectedSeasonKey] = useState(() => {
    const today = new Date();
    const nextBlock = seasons.find((season) => season.anchor > today);
    return (nextBlock || seasons[0])?.key || '';
  });
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planOverrides, setPlanOverrides] = useState({});
  const [saveError, setSaveError] = useState('');
  const [editingCard, setEditingCard] = useState(null);
  const [historyCard, setHistoryCard] = useState(null);
  const [approvingCard, setApprovingCard] = useState(null);
  const [unapprovingCard, setUnapprovingCard] = useState(null);
  const [viewMode, setViewMode] = useState('board');
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const showInvoicing = useMemo(
    () => canSeeCycleInvoicing((readMaintenanceUser() || {}).role),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (buildings.length > 0) setRefreshing(true);
      else setLoading(true);
      setError('');
      try {
        const [buildingsResponse, plansResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/api/maintenance-schedule/buildings`),
          fetch(`${API_BASE_URL}/api/maintenance-seasonal-plans`),
        ]);
        if (!buildingsResponse.ok) throw new Error(`Contracts request failed (${buildingsResponse.status})`);
        if (!plansResponse.ok) throw new Error(`Plans request failed (${plansResponse.status})`);
        const [buildingsBody, plansBody] = await Promise.all([
          buildingsResponse.json(),
          plansResponse.json(),
        ]);
        if (!cancelled) {
          setBuildings(Array.isArray(buildingsBody?.buildings) ? buildingsBody.buildings : []);
          const sharedPlans = {};
          (Array.isArray(plansBody?.plans) ? plansBody.plans : []).forEach((plan) => {
            if (plan?.planKey) sharedPlans[plan.planKey] = plan;
          });
          setPlanOverrides(sharedPlans);
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Unable to load maintenance contracts.');
      } finally {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const plansBySeason = useMemo(() => {
    const result = {};
    timeline.forEach((season) => {
      // `seasons` stays the allSeasons argument even for a historical target,
      // so the due-date rules keyed on allSeasons[0] behave as they always did.
      result[season.key] = buildSeasonCards(buildings, season, seasons, planOverrides).map((card) => {
        const override = planOverrides[`${season.key}:${card.buildingId}`] || {};
        // A plan can be approved with dates that land outside the season's
        // calendar window (e.g. a Fall 2026 cycle approved for January).
        // Season-matching the inspection date would never find those, so
        // the plan's own approval stamp is the authoritative signal.
        const approvedViaPlan = card.approvedPlanCoversAll;
        const managementApproved = card.status === 'scheduled' || approvedViaPlan;
        const authoritativeInspection = card.approvedInspection
          || (!card.approvedInspectionId && (card.scheduledPrimary || card.completedPrimary)) || {};
        const hasOverrideStart = !managementApproved && Boolean(override.start);
        const hasOverrideEnd = !managementApproved && Boolean(override.end);
        const datesSent = Boolean(override.datesSent || override.approved);
        const skippedThisCycle = card.lockedContractTypes.length === 0 && Boolean(override.skippedThisCycle);
        const filterType = managementApproved ? card.filterType : (override.filterType ?? card.filterType);
        const filterSize = managementApproved ? card.filterSize : (override.filterSize ?? card.filterSize);
        const filterQuantity = managementApproved ? card.filterQuantity : (override.filterQuantity ?? card.filterQuantity);
        // The building's Jobber link (set in Manage Accounts) is the source
        // of truth. A plan snapshot saved before the building was connected
        // stores empty strings, which must never shadow the live link. The
        // override trio only applies to buildings with no connection yet.
        const buildingHasJobber = Boolean(card.jobberClientId);
        return {
          ...card,
          planUpdatedAt: override.updatedAt || '',
          start: hasOverrideStart ? parseLocalDate(override.start) : card.start,
          end: hasOverrideEnd ? parseLocalDate(override.end) : card.end,
          crew: override.crew || '',
          planningNotes: override.notes || '',
          filterType,
          filterSize,
          filterQuantity,
          filter: formatFilterDetails(filterType, filterSize, filterQuantity),
          dailySchedule: managementApproved
            ? (authoritativeInspection.dailySchedule || '')
            : (override.dailySchedule || ''),
          pricingDetails: managementApproved
            ? (authoritativeInspection.pricingDetails || '')
            : (override.pricingDetails || ''),
          maintenanceId: maintenanceIdForPlan(card, override, managementApproved, authoritativeInspection),
          jobberClientId: buildingHasJobber ? card.jobberClientId : (override.jobberClientId || ''),
          jobberPropertyId: buildingHasJobber ? card.jobberPropertyId : (override.jobberPropertyId || ''),
          jobberClientName: buildingHasJobber ? card.jobberClientName : (override.jobberClientName || ''),
          datesSent,
          skippedThisCycle,
          skippedAt: override.skippedAt || '',
          skipReason: override.skipReason || '',
          status: (skippedThisCycle || card.allScopesSkipped) ? 'skipped'
            : (card.status === 'scheduled' || approvedViaPlan) ? 'scheduled'
            : datesSent ? 'sent'
            : card.status,
        };
      });
    });
    return result;
  }, [buildings, seasons, timeline, planOverrides]);

  const updatePlan = async (season, card, patch, options = {}) => {
    const key = `${season.key}:${card.buildingId}`;
    const previous = planOverrides[key] || {};
    // Persist a complete snapshot even for one-click workflow actions such
    // as "Mark dates sent". Once dates are sent, the exact recommendation
    // the building received must not drift if source history later changes.
    const cardSnapshot = {
      start: toIso(card.start) || '',
      end: toIso(card.end) || '',
      crew: card.crew || '',
      notes: card.planningNotes || '',
      filterType: card.filterType || '',
      filterSize: card.filterSize || '',
      filterQuantity: card.filterQuantity || '',
      dailySchedule: card.dailySchedule || '',
      pricingDetails: card.pricingDetails || '',
      maintenanceId: card.maintenanceId || nextMaintenanceId(card),
      jobberClientId: card.jobberClientId || '',
      jobberPropertyId: card.jobberPropertyId || '',
      jobberClientName: card.jobberClientName || '',
      datesSent: Boolean(card.datesSent),
      skippedThisCycle: Boolean(card.skippedThisCycle),
      skippedAt: card.skippedAt || '',
      skipReason: card.skipReason || '',
      scopeDecisions: card.scopeDecisions || {},
    };
    const nextPlan = { ...cardSnapshot, ...previous, ...patch };
    setSaveError('');
    setPlanOverrides((current) => ({ ...current, [key]: { ...(current[key] || {}), ...patch } }));
    const user = readMaintenanceUser() || {};
    try {
      const response = await fetch(`${API_BASE_URL}/api/maintenance-seasonal-plans`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...nextPlan,
          expectedUpdatedAt: options.expectedUpdatedAt ?? (previous.updatedAt || ''),
          planKey: key,
          seasonKey: season.key,
          seasonName: season.name,
          seasonYear: season.year,
          seasonLabel: season.label,
          buildingId: String(card.buildingId),
          buildingObjectId: card.objectId || '',
          buildingName: card.name || '',
          updatedBy: {
            name: user.name || user.fullName || '',
            email: user.email || '',
            role: user.role || '',
          },
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body?.error || `Save failed (${response.status})`);
      setPlanOverrides((current) => ({
        ...current,
        [key]: { ...(current[key] || {}), ...(body?.plan || nextPlan) },
      }));
      // Attaching a Jobber client from the planner also connects it at the
      // building level (same as Manage Accounts), so the link is shared by
      // every season and every future scheduled maintenance.
      if (
        'jobberClientId' in patch
        && card.objectId
        && patch.jobberClientId
        && patch.jobberClientId !== (card.jobberClientId || '')
      ) {
        const buildingJobber = {
          jobberClientId: patch.jobberClientId || '',
          jobberPropertyId: patch.jobberPropertyId || '',
          jobberClientName: patch.jobberClientName || '',
        };
        const buildingResponse = await fetch(`${API_BASE_URL}/api/buildings/${card.objectId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(buildingJobber),
        });
        if (buildingResponse.ok) {
          setBuildings((current) => current.map((row) => (
            row.objectId === card.objectId ? { ...row, ...buildingJobber } : row
          )));
        }
      }
      return true;
    } catch (err) {
      setPlanOverrides((current) => ({ ...current, [key]: previous }));
      setSaveError(err?.message || 'Could not save the planning record.');
      if (options.throwOnError) throw err;
      return false;
    }
  };

  const selectedSeason = timeline.find((season) => season.key === selectedSeasonKey) || seasons[0];
  const cards = useMemo(
    () => plansBySeason[selectedSeason?.key] || [],
    [plansBySeason, selectedSeason?.key],
  );
  useEffect(() => {
    const firstDatedCard = [...cards]
      .filter((card) => card.start)
      .sort((a, b) => a.start - b.start)[0];
    setCalendarMonth(new Date(firstDatedCard?.start || selectedSeason?.anchor || new Date()));
  }, [selectedSeason?.key, selectedSeason?.anchor, cards]);
  const invoiceState = useCycleInvoices(selectedSeason, showInvoicing, reloadKey);
  // Only 'ready'/'revalidating' mean Jobber actually answered. Every
  // billing-derived filter, sort and label keys off this so a failed or
  // still-warming fetch never masquerades as "nothing was invoiced".
  const invoicesKnown = invoiceState.status === 'ready' || invoiceState.status === 'revalidating';

  const visibleCards = useMemo(() => {
    const query = search.trim().toLowerCase();
    return cards.filter((card) => {
      if (statusFilter === 'scheduled' && card.status !== 'scheduled') return false;
      if (statusFilter === 'planning' && card.status !== 'planning') return false;
      if (statusFilter === 'sent' && card.status !== 'sent') return false;
      if (statusFilter === 'skipped' && card.status !== 'skipped') return false;
      if (!query) return true;
      return `${card.name} ${card.address} ${card.buildingId}`.toLowerCase().includes(query);
    });
  }, [cards, search, statusFilter]);

  const stats = useMemo(() => ({
    total: cards.length,
    scheduled: cards.filter((card) => card.status === 'scheduled').length,
    sent: cards.filter((card) => card.status === 'sent').length,
    planning: cards.filter((card) => card.status === 'planning').length,
    skipped: cards.filter((card) => card.status === 'skipped').length,
    units: cards.reduce((sum, card) => sum + (card.units || 0), 0),
  }), [cards]);

  // Cycle billing roll-up for the season pill. Each Jobber client counts
  // once — two towers can share one client (155 Merchants Wharf North and
  // South do), and summing per building would double their invoices.
  const invoiceStats = useMemo(() => {
    if (!showInvoicing || !invoicesKnown) return null;
    const byClient = invoiceState.byClient || {};
    const counted = new Set();
    let total = 0;
    let count = 0;
    let invoiced = 0;
    let unlinked = 0;
    cards.forEach((card) => {
      if (!card.jobberClientId) {
        if (card.status !== 'skipped') unlinked += 1;
        return;
      }
      const bucket = byClient[card.jobberClientId];
      if ((bucket?.count || 0) > 0) invoiced += 1;
      if (counted.has(card.jobberClientId)) return;
      counted.add(card.jobberClientId);
      total += bucket?.total || 0;
      count += bucket?.count || 0;
    });
    const cycleTotal = invoiceState.summary?.total || 0;
    return {
      total,
      count,
      invoiced,
      unlinked,
      // Maintenance billing in this window that belongs to a client not
      // planned in this block (a one-off, or a building whose contract
      // sits in the other cycle).
      outsideBlock: Math.max(0, Math.round((cycleTotal - total) * 100) / 100),
    };
  }, [showInvoicing, invoicesKnown, cards, invoiceState.byClient, invoiceState.summary]);

  // Before approval the planning snapshot IS the plan, so Adjust only has to
  // write there. Approval hands authority to the MaintenanceInspections
  // record — that is what the live schedule, the field app and Jobber all
  // read, and what this page reads back for an approved card. So an edit
  // made after approval has to write through to the inspection too, or it
  // lands on a snapshot nobody reads any more. That's the whole reason dates
  // and the daily schedule appeared to save but never moved, while notes
  // (which live only on the snapshot) worked fine.
  const syncApprovedInspection = async (season, card, patch) => {
    const key = `${season.key}:${card.buildingId}`;
    const inspection = await updateSeasonalInspection(card, planOverrides[key] || {}, patch);
    if (!inspection) return;
    const inspectionId = inspection.objectId;

    // schedule_inspection() reschedules the existing visit when the job is
    // already on Jobber, so this same call covers a date change. Only worth
    // making for a cycle that hasn't started yet — same rule the Schedule
    // page uses.
    const startsAhead = (patch.start || toIso(card.start) || '') >= new Date().toISOString().slice(0, 10);
    if (!card.jobberClientId || !startsAhead) return;
    try {
      const sync = await fetch(
        `${API_BASE_URL}/api/maintenance-inspections/${encodeURIComponent(inspectionId)}/jobber-schedule`,
        { method: 'POST' },
      );
      const body = await sync.json().catch(() => ({}));
      // inProgress = a backend hook is already pushing this one; that's a
      // success from the office's point of view.
      if (!body.ok && !body.inProgress) {
        setSaveError(`Dates saved, but Jobber did not update: ${body.error || 'unknown error'}.`);
      }
    } catch (err) {
      setSaveError(`Dates saved, but Jobber did not update: ${err?.message || 'network error'}.`);
    }
  };

  const saveApprovedSchedule = async (inspectionData) => {
    const key = `${selectedSeason.key}:${approvingCard.buildingId}`;
    try {
      return await approveSeasonalSchedule({
        card: approvingCard,
        season: selectedSeason,
        inspectionData,
        retainedJobId: planOverrides[key]?.retainedJobberJobId || '',
        savePlan: patch => updatePlan(selectedSeason, approvingCard, patch, {
          expectedUpdatedAt: approvingCard.planUpdatedAt || '',
          throwOnError: true,
        }),
        onRecoveredPlan: plan => {
          setPlanOverrides(current => ({ ...current, [key]: plan }));
          setSaveError('');
        },
      });
    } catch (err) {
      setSaveError(err.message || 'Could not approve this seasonal schedule.');
      // Close stale approval input before reloading the saved inspection, so
      // the user cannot immediately submit the old scope a second time.
      setApprovingCard(null);
      throw err;
    } finally {
      setReloadKey((value) => value + 1);
    }
  };

  // Walking back an approval. A card reads as management-approved because an
  // upcoming MaintenanceInspections record exists, so returning it to
  // "dates sent" means removing that record — there's no softer version.
  // The Jobber visit goes first: if that fails we stop, because deleting the
  // inspection would otherwise strand a booked visit nobody can trace back.
  const unapproveCard = async (season, card) => {
    const key = `${season.key}:${card.buildingId}`;
    setSaveError('');
    try {
      const released = await releaseSeasonalInspection(card, planOverrides[key] || {});

      // Seed the reopened plan with the dates that were actually approved,
      // so the card comes back showing what management last saw rather than
      // whatever stale proposal predated the approval.
      const reopened = await updatePlan(season, card, {
        approvedInspectionId: '',
        approvedAt: '',
        retainedJobberJobId: released.jobId,
        datesSent: true,
        start: toIso(card.start) || '',
        end: toIso(card.end) || '',
        dailySchedule: card.dailySchedule || '',
        pricingDetails: card.pricingDetails || '',
      });
      setReloadKey((value) => value + 1);
      if (!reopened) throw new Error('The booked visit was removed, but its seasonal plan could not be reopened. Refresh and review this building before scheduling again.');
      return true;
    } catch (err) {
      setSaveError(err?.message || 'Could not move this building back.');
      return false;
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: sharedFont, color: C.ink }}>
      <style>{`
        @keyframes seasonal-spin { to { transform: rotate(360deg); } }
        .season-card:hover { transform: translateY(-1px); box-shadow: 0 8px 26px rgba(27,45,80,.09) !important; }
        .season-tab:hover { border-color: #9db3d2 !important; }
        @media (max-width: 900px) {
          .seasonal-page { padding: 20px 14px 44px !important; }
          .seasonal-stats { grid-template-columns: repeat(2, minmax(0,1fr)) !important; }
          .seasonal-card-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <main className="seasonal-page" style={{ maxWidth: 1320, margin: '0 auto', padding: '24px 24px 64px' }}>
        <header style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
          gap: 18, flexWrap: 'wrap', marginBottom: 18,
        }}>
          <div>
            <div style={{
              color: C.muted, fontSize: 12.5, fontWeight: 600,
              letterSpacing: '.06em', textTransform: 'uppercase',
            }}>
              Hudson Condominium Solutions Inc.
            </div>
            <h1 style={{
              fontSize: 26, fontWeight: 700, color: C.navy,
              margin: '4px 0 4px', letterSpacing: '-0.01em',
            }}>
              Seasonal Planner
            </h1>
            <div style={{ color: C.muted, fontSize: 13 }}>
              Plan upcoming maintenance cycles, send dates, and approve schedules in one place.
            </div>
          </div>
          <button
            type="button"
            onClick={() => setReloadKey((value) => value + 1)}
            disabled={refreshing}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              border: `1px solid ${C.line}`, background: C.card, color: C.inkSoft,
              borderRadius: 8, padding: '9px 12px', fontWeight: 650, fontSize: 13,
              cursor: refreshing ? 'wait' : 'pointer', fontFamily: 'inherit',
            }}
          >
            <FaSyncAlt style={{ animation: refreshing ? 'seasonal-spin 1s linear infinite' : 'none' }} />
            Refresh contracts
          </button>
        </header>

        <section className="seasonal-summary-row" style={{
          display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 18,
        }}>
          <PlannerMetricCard
            label="Buildings in block"
            value={loading ? '—' : stats.total}
            sublabel={`Contracts in ${selectedSeason?.label || 'selected cycle'}`}
            accent={C.navy}
            icon={<FaCalendarAlt />}
          />
          <PlannerMetricCard
            label="Needs Scheduling"
            value={loading ? '—' : stats.planning}
            sublabel="Dates still need action"
            accent={C.red}
            icon={<FaExclamationTriangle />}
          />
          <PlannerMetricCard
            label="Dates sent"
            value={loading ? '—' : stats.sent}
            sublabel="Awaiting management approval"
            accent={C.amber}
            icon={<FaClipboardCheck />}
          />
          <PlannerMetricCard
            label="Management Approved"
            value={loading ? '—' : stats.scheduled}
            sublabel="Ready on the live schedule"
            accent={C.green}
            icon={<FaCheckCircle />}
            background={C.greenSoft}
          />
          <div style={{
            flex: '1.25 1 275px', minWidth: 0,
            background: `linear-gradient(135deg, ${C.navy} 0%, #16294C 100%)`,
            color: '#fff', borderRadius: 12, padding: '18px 22px',
            boxShadow: C.shadow, display: 'flex', flexDirection: 'column',
            justifyContent: 'space-between', gap: 12,
          }}>
            <div>
              <div style={{
                fontSize: 12.5, fontWeight: 600, letterSpacing: '.04em',
                textTransform: 'uppercase', opacity: .85,
              }}>
                {selectedSeason?.label || 'Season overview'}
              </div>
              <div style={{ fontSize: 20, lineHeight: 1.15, fontWeight: 700, marginTop: 5 }}>
                {selectedSeason?.name === 'Fall' ? 'September–January' : 'February–August'}
              </div>
              <div style={{ fontSize: 12, opacity: .78, marginTop: 4 }}>Planning cycle</div>
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 14 }}>
                <div>
                  <div style={{ fontSize: showInvoicing ? 23 : 27, lineHeight: 1, fontWeight: 750 }}>
                    {loading ? '—' : stats.units.toLocaleString()}
                  </div>
                  <div style={{ fontSize: 12, opacity: .8, marginTop: 5 }}>Total units</div>
                </div>
                {showInvoicing && (
                  <div style={{ textAlign: 'right', minWidth: 0 }}>
                    <div style={{ fontSize: 23, lineHeight: 1, fontWeight: 750 }}>
                      {invoiceState.status === 'loading' ? '…' : invoicesKnown ? formatMoney(invoiceStats?.total) : '—'}
                    </div>
                    <div style={{ fontSize: 12, opacity: .8, marginTop: 5 }}>
                      Invoiced this cycle
                    </div>
                  </div>
                )}
              </div>
              {showInvoicing && (
                <div style={{ fontSize: 11, opacity: .72, marginTop: 8, lineHeight: 1.45 }}>
                  {invoiceState.status === 'loading'
                    ? 'Pulling maintenance invoices from Jobber…'
                    : !invoicesKnown
                      ? 'Jobber invoices unavailable right now.'
                      : (
                        <>
                          {invoiceStats?.invoiced || 0} of {stats.total} buildings invoiced
                          {' · '}
                          {invoiceStats?.count || 0} maintenance invoice{invoiceStats?.count === 1 ? '' : 's'}
                          {invoiceState.status === 'revalidating' ? ' · refreshing…' : ''}
                        </>
                      )}
                </div>
              )}
            </div>
          </div>
        </section>

        <section style={{
          background: C.card, border: `1px solid ${C.line}`, borderRadius: 12,
          padding: '14px 18px', marginBottom: 14, boxShadow: C.shadow,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: 12,
          }}>
            <div style={{
              display: 'flex', gap: 8, alignItems: 'stretch',
              width: '100%', boxSizing: 'border-box',
            }}>
              <button
                type="button"
                aria-label="Earlier cycles"
                title="Earlier cycles"
                disabled={windowStart <= 0}
                onClick={() => setWindowStart((start) => Math.max(0, start - 1))}
                style={{
                  flex: '0 0 32px', border: `1px solid ${C.line}`, background: C.card,
                  borderRadius: 9, cursor: windowStart <= 0 ? 'default' : 'pointer',
                  color: windowStart <= 0 ? C.line : C.muted, fontSize: 16,
                  fontFamily: 'inherit', lineHeight: 1, padding: 0,
                }}
              >
                ‹
              </button>
            <div style={{
              display: 'flex', gap: 4, background: C.lineSoft,
              padding: 4, borderRadius: 10, overflowX: 'auto',
              width: '100%', boxSizing: 'border-box',
            }}>
              {visibleSeasons.map((season) => {
                const active = season.key === selectedSeason?.key;
                const seasonCards = plansBySeason[season.key] || [];
                const approved = seasonCards.filter((card) => card.status === 'scheduled').length;
                return (
                  <button
                    type="button"
                    key={season.key}
                    onClick={() => setSelectedSeasonKey(season.key)}
                    style={{
                      background: active ? C.navy : 'transparent',
                      border: 'none', padding: '8px 13px', fontWeight: 650,
                      color: active ? '#fff' : C.muted, fontSize: 12.5,
                      borderRadius: 8, cursor: 'pointer', whiteSpace: 'nowrap',
                      boxShadow: active ? '0 1px 3px rgba(20,30,60,.12)' : 'none',
                      fontFamily: 'inherit',
                      textAlign: 'left',
                      flex: '1 1 0',
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 700 }}>
                      <span style={{ marginRight: 5 }}>{season.name === 'Spring' ? '☀️' : '🍂'}</span>
                      {season.label}
                    </div>
                    <div style={{
                      fontSize: 10.5, fontWeight: 550, marginTop: 3,
                      opacity: active ? .78 : .7,
                    }}>
                      {season.isPast
                        ? `${seasonCards.length} buildings · completed cycle`
                        : `${seasonCards.length} buildings · ${approved} management approved`}
                    </div>
                  </button>
                );
              })}
            </div>
              <button
                type="button"
                aria-label="Later cycles"
                title="Later cycles"
                disabled={windowStart >= maxWindowStart}
                onClick={() => setWindowStart((start) => Math.min(maxWindowStart, start + 1))}
                style={{
                  flex: '0 0 32px', border: `1px solid ${C.line}`, background: C.card,
                  borderRadius: 9,
                  cursor: windowStart >= maxWindowStart ? 'default' : 'pointer',
                  color: windowStart >= maxWindowStart ? C.line : C.muted, fontSize: 16,
                  fontFamily: 'inherit', lineHeight: 1, padding: 0,
                }}
              >
                ›
              </button>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{
                display: 'flex', gap: 4, background: C.lineSoft,
                padding: 4, borderRadius: 9, flexWrap: 'wrap',
              }}>
                {[
                  ['all', 'All', C.navy, '#fff'],
                  ['planning', 'Needs Scheduling', C.redSoft, C.red],
                  ['sent', 'Dates sent', C.amberSoft, C.amber],
                  ['scheduled', 'Management Approved', C.greenSoft, C.green],
                  ['skipped', 'Skipped', C.lineSoft, C.muted],
                ].map(([value, label, activeBackground, activeColor]) => {
                  const active = statusFilter === value;
                  return (
                    <button
                      type="button"
                      key={value}
                      onClick={() => setStatusFilter(value)}
                      style={{
                        background: active ? activeBackground : 'transparent',
                        border: `1px solid ${active && value !== 'all' ? `${activeColor}30` : 'transparent'}`,
                        color: active ? activeColor : C.muted,
                        borderRadius: 7, padding: '6px 9px', fontSize: 12,
                        fontWeight: active ? 750 : 650, cursor: 'pointer', fontFamily: 'inherit',
                        whiteSpace: 'nowrap',
                        boxShadow: active ? '0 1px 2px rgba(20,30,60,.06)' : 'none',
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: 4, background: C.lineSoft, padding: 4, borderRadius: 9 }}>
                {[
                  ['board', 'Board'],
                  ['calendar', 'Calendar'],
                ].map(([value, label]) => (
                  <button
                    type="button"
                    key={value}
                    onClick={() => setViewMode(value)}
                    style={{
                      background: viewMode === value ? '#fff' : 'transparent',
                      border: 'none', color: viewMode === value ? C.navy : C.muted,
                      borderRadius: 7, padding: '6px 10px', fontSize: 12,
                      fontWeight: 650, cursor: 'pointer', fontFamily: 'inherit',
                      boxShadow: viewMode === value ? '0 1px 3px rgba(20,30,60,.08)' : 'none',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div style={{ position: 'relative' }}>
                <FaSearch style={{
                  position: 'absolute', left: 12, top: 12,
                  color: C.muted, fontSize: 12,
                }} />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search building or ID…"
                  style={{
                    border: `1px solid ${C.line}`, borderRadius: 8, background: '#fff',
                    color: C.ink, padding: '9px 10px 9px 32px', width: 235,
                    fontSize: 12.5, fontFamily: 'inherit', outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        {showInvoicing && invoiceState.status !== 'loading'
          && Boolean(invoiceStats?.outsideBlock > 0 || invoiceStats?.unlinked > 0) && (
          <div style={{
            marginBottom: 14, padding: '10px 13px', borderRadius: 9,
            background: C.lineSoft, border: `1px solid ${C.line}`,
            color: C.muted, fontSize: 11.5, lineHeight: 1.55,
          }}>
            {invoiceStats.unlinked > 0 && (
              <div>
                <strong style={{ color: C.inkSoft }}>{invoiceStats.unlinked}</strong>
                {' '}building{invoiceStats.unlinked === 1 ? '' : 's'} in this block
                {invoiceStats.unlinked === 1 ? ' has' : ' have'} no Jobber client attached,
                so their invoices can&rsquo;t be matched.
              </div>
            )}
            {invoiceStats.outsideBlock > 0 && (
              <div>
                <strong style={{ color: C.inkSoft }}>{formatMoney(invoiceStats.outsideBlock)}</strong>
                {' '}of maintenance billing landed in this window on clients that
                aren&rsquo;t planned in this block — one-offs, or buildings whose
                contract sits in the other cycle.
              </div>
            )}
          </div>
        )}

        {error && (
          <div style={{
            marginTop: 18, padding: '13px 15px', borderRadius: 10,
            background: C.redSoft, border: '1px solid #f3c3c7', color: C.red, fontSize: 13,
          }}>
            <strong>Could not load the planner.</strong> {error}
          </div>
        )}
        {saveError && (
          <div style={{
            marginTop: 18, padding: '13px 15px', borderRadius: 10,
            background: C.redSoft, border: '1px solid #f3c3c7', color: C.red, fontSize: 13,
          }}>
            <strong>Could not save the planner.</strong> {saveError}
          </div>
        )}

        {loading ? (
          <LoadingState />
        ) : visibleCards.length === 0 ? (
          <EmptyState hasFilters={Boolean(search || statusFilter !== 'all')} />
        ) : viewMode === 'calendar' ? (
          <ProposedCalendar
            cards={visibleCards}
            month={calendarMonth}
            onMonthChange={setCalendarMonth}
            onOpen={(card) => setEditingCard(card)}
          />
        ) : (
          <section className="seasonal-card-grid" style={{
            display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))',
            gap: 12, marginTop: 16,
          }}>
            {visibleCards.map((card) => (
              <PlanningCard
                key={`${selectedSeason?.key}-${card.buildingId}`}
                card={card}
                season={selectedSeason}
                onDatesSent={() => updatePlan(selectedSeason, card, { datesSent: true })}
                onApproveSchedule={() => setApprovingCard(card)}
                onAdjust={() => setEditingCard(card)}
                onHistory={() => setHistoryCard(card)}
                onUnapprove={() => setUnapprovingCard(card)}
              />
            ))}
          </section>
        )}
      </main>
      {editingCard && (
        <PlanEditModal
          card={editingCard}
          season={selectedSeason}
          onClose={() => setEditingCard(null)}
          onSave={async (patch) => {
            if (editingCard.status === 'scheduled') {
              const target = seasonalInspectionTarget(editingCard, planOverrides[`${selectedSeason.key}:${editingCard.buildingId}`] || {});
              if (!target?.objectId) throw new Error('Refresh or review the approved maintenance in Schedule before changing it.');
            }
            const saved = await updatePlan(selectedSeason, editingCard, patch);
            if (!saved) throw new Error('The planning record could not be saved.');
            if (editingCard.status === 'scheduled') {
              await syncApprovedInspection(selectedSeason, editingCard, patch);
              setReloadKey((value) => value + 1);
            }
            setEditingCard(null);
          }}
        />
      )}
      {historyCard && <HistoryModal card={historyCard} onClose={() => setHistoryCard(null)} />}
      {unapprovingCard && (
        <UnapproveModal
          card={unapprovingCard}
          season={selectedSeason}
          onClose={() => setUnapprovingCard(null)}
          onConfirm={async () => {
            const moved = await unapproveCard(selectedSeason, unapprovingCard);
            if (!moved) throw new Error('This building could not be moved back.');
            setUnapprovingCard(null);
          }}
        />
      )}
      {approvingCard && (
        <ScheduleMaintenanceModal
          building={{
            ...(approvingCard.rows?.find(row => !approvingCard.lockedContractTypes.includes(row.contractType)) || {}),
            upcomingInspection: null,
            objectId: approvingCard.objectId,
            buildingId: approvingCard.buildingId,
            name: approvingCard.name,
            address: approvingCard.address,
            maintenanceHistory: approvingCard.history,
            filterType: approvingCard.filterType,
            filterSize: approvingCard.filterSize,
            filterQuantity: approvingCard.filterQuantity,
            jobberClientId: approvingCard.jobberClientId,
            jobberPropertyId: approvingCard.jobberPropertyId,
            jobberClientName: approvingCard.jobberClientName,
          }}
          proposal={{
            seasonalApproval: true,
            maintenanceId: approvingCard.maintenanceId,
            startDate: toIso(approvingCard.start),
            endDate: toIso(approvingCard.end),
            cycle: selectedSeason.label,
            maintenanceTypes: maintenanceTypesForCard(approvingCard),
            skippedContractTypes: [
              ...(approvingCard.skippedScopes || []).map(item => item.row.contractType),
              ...(approvingCard.lockedContractTypes || []),
            ],
            dailySchedule: approvingCard.dailySchedule,
            pricingDetails: approvingCard.pricingDetails,
            filterType: approvingCard.filterType,
            filterSize: approvingCard.filterSize,
            filterQuantity: approvingCard.filterQuantity,
          }}
          onClose={() => setApprovingCard(null)}
          onSave={saveApprovedSchedule}
          onDelete={() => {}}
        />
      )}
    </div>
  );
}

function PlannerMetricCard({ label, value, sublabel, accent = C.navy, icon, background = C.card }) {
  return (
    <div style={{
      background, border: `1px solid ${C.line}`, borderRadius: 12,
      padding: '18px 22px', boxShadow: C.shadow, flex: '1 1 190px',
      minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, color: C.muted,
        fontSize: 12.5, fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '.04em',
      }}>
        {icon}
        <span>{label}</span>
      </div>
      <div style={{ fontSize: 30, fontWeight: 700, color: accent, lineHeight: 1.1 }}>
        {value}
      </div>
      {sublabel ? <div style={{ fontSize: 12.5, color: C.muted }}>{sublabel}</div> : null}
    </div>
  );
}

function ProposedCalendar({ cards, month, onMonthChange, onOpen }) {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const first = new Date(year, monthIndex, 1);
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const mondayOffset = (first.getDay() + 6) % 7;
  const cells = Array.from({ length: Math.ceil((mondayOffset + daysInMonth) / 7) * 7 }, (_, index) => {
    const day = index - mondayOffset + 1;
    return day >= 1 && day <= daysInMonth ? new Date(year, monthIndex, day) : null;
  });
  const palette = {
    planning: { bg: C.redSoft, fg: C.red },
    sent: { bg: C.amberSoft, fg: C.amber },
    scheduled: { bg: C.greenSoft, fg: C.green },
  };

  return (
    <section style={{
      marginTop: 16, background: C.card, border: `1px solid ${C.line}`,
      borderRadius: 12, boxShadow: C.shadow, overflow: 'hidden',
    }}>
      <div style={{
        padding: '12px 14px', borderBottom: `1px solid ${C.line}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <button
          type="button"
          aria-label="Previous month"
          onClick={() => onMonthChange(new Date(year, monthIndex - 1, 1))}
          style={calendarNavButton}
        ><FaChevronLeft /></button>
        <div style={{ color: C.navy, fontSize: 16, fontWeight: 800 }}>
          {month.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
        </div>
        <button
          type="button"
          aria-label="Next month"
          onClick={() => onMonthChange(new Date(year, monthIndex + 1, 1))}
          style={calendarNavButton}
        ><FaChevronRight /></button>
      </div>
      <div className="seasonal-calendar-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0,1fr))' }}>
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
          <div className="seasonal-calendar-weekday" key={day} style={{
            padding: '7px 6px', textAlign: 'center', color: C.muted,
            fontSize: 10.5, fontWeight: 750, textTransform: 'uppercase',
            borderBottom: `1px solid ${C.line}`,
          }}>{day}</div>
        ))}
        {cells.map((date, index) => {
          const dateIso = date ? toIso(date) : '';
          const activeCards = date
            ? cards.filter((card) => {
              const startIso = toIso(card.start);
              const endIso = toIso(card.end || card.start);
              return startIso && dateIso >= startIso && dateIso <= endIso;
            })
            : [];
          return (
            <div className="seasonal-calendar-day" data-date={date ? date.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' }) : ''} key={`${dateIso || 'empty'}-${index}`} style={{
              minHeight: 105, padding: 6, background: date ? '#fff' : C.lineSoft,
              borderRight: (index + 1) % 7 ? `1px solid ${C.lineSoft}` : 0,
              borderBottom: `1px solid ${C.lineSoft}`,
            }}>
              {date && <div className="seasonal-calendar-date" style={{ color: C.muted, fontSize: 10.5, fontWeight: 700 }}>{date.getDate()}</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 5 }}>
                {activeCards.map((card) => {
                  const tone = palette[card.status] || palette.planning;
                  const isStart = toIso(card.start) === dateIso;
                  const isEnd = toIso(card.end || card.start) === dateIso;
                  return (
                    <button
                      type="button"
                      key={`${card.buildingId}-${dateIso}`}
                      onClick={() => onOpen(card)}
                      title={`${card.name} · ${formatWindow(card.start, card.end)}`}
                      style={{
                        border: 0, borderRadius: 5, padding: '4px 5px',
                        background: tone.bg, color: tone.fg, textAlign: 'left',
                        fontSize: 10.5, fontWeight: 700, cursor: 'pointer',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}
                    >
                      {card.name}{isStart ? ' · Start' : isEnd ? ' · End' : ''}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

const calendarNavButton = {
  border: `1px solid ${C.line}`, background: '#fff', color: C.navy,
  width: 30, height: 30, borderRadius: 7, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};


export function PlanningCard({ card, season, onDatesSent, onApproveSchedule, onAdjust, onHistory, onUnapprove }) {
  const [emailCopyState, setEmailCopyState] = useState('idle');
  const partialApproval = card.partiallyScheduled || card.partiallyCompleted;
  const status = statusMeta(partialApproval ? 'partial' : card.status);
  const workingDays = workingDaysBetween(card.start, card.end);
  const isFinal = !card.renewalRequired
    && (card.expiredContract || card.expiresBeforeVisit || card.expiringAfterMaintenance);
  const hasAttention = card.needsScopeReview || card.partiallyScheduled || card.partiallyCompleted
    || card.renewalRequired || card.expiresBeforeVisit || card.extraCyclesRequired || !card.start;
  const windowTone = card.status === 'skipped'
    ? { bg: C.lineSoft, fg: C.muted }
    : card.status === 'scheduled'
    ? { bg: C.greenSoft, fg: C.green }
    : card.status === 'sent'
      ? { bg: C.amberSoft, fg: C.amber }
      : { bg: C.redSoft, fg: C.red };
  const windowLabel = card.status === 'skipped'
      ? `${season.label} cycle skipped`
      : card.status === 'scheduled'
      ? 'Management-approved schedule'
      : card.status === 'sent'
        ? 'Dates sent to building'
        : `Proposed ${season.label} window`;

  return (
    <article className="season-card" style={{
      background: C.card, border: `1px solid ${hasAttention ? '#eccd91' : C.line}`,
      borderRadius: 12, boxShadow: C.shadow, overflow: 'hidden',
      transition: 'transform 100ms ease, box-shadow 140ms ease',
      opacity: card.status === 'skipped' ? .86 : 1,
    }}>
      <div style={{ padding: '15px 16px 13px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h3 style={{
                margin: 0, color: C.ink, fontSize: 15.5, fontWeight: 780,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {card.name}
              </h3>
              {card.buildingId && (
                <span style={{
                  background: C.lineSoft, color: C.muted, borderRadius: 5,
                  padding: '2px 6px', fontSize: 10.5, fontWeight: 700,
                }}>
                  #{card.buildingId}
                </span>
              )}
            </div>
            <div style={{ color: C.muted, fontSize: 11.5, marginTop: 3 }}>
              {[card.address, card.units ? `${card.units.toLocaleString()} units` : null].filter(Boolean).join(' · ')}
            </div>
          </div>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: status.bg, color: status.fg, borderRadius: 999,
            padding: '5px 8px', fontSize: 10.5, fontWeight: 750,
            textTransform: 'uppercase', letterSpacing: '.035em', whiteSpace: 'nowrap',
          }}>
            {status.icon}{status.label}
          </span>
        </div>

        {card.renewalRequired && (
          <div style={{
            marginTop: 10, padding: '8px 10px', borderRadius: 8,
            color: C.red, background: C.redSoft, border: '1px solid #f3c3c7',
            display: 'flex', alignItems: 'center', gap: 7,
            fontSize: 11.5, fontWeight: 750,
          }}>
            <FaExclamationTriangle style={{ flexShrink: 0 }} />
            <span>
              {card.expiredContract ? 'CONTRACT ENDED' : 'CONTRACT FULFILLED'} — RENEWAL REQUIRED
              {card.earliestExpiry ? ` · ${formatDate(card.earliestExpiry)}` : ''}
            </span>
          </div>
        )}

        {card.status === 'skipped' && (
          <div style={{
            marginTop: 10, padding: '8px 10px', borderRadius: 8,
            color: C.inkSoft, background: C.lineSoft, border: `1px solid ${C.line}`,
            display: 'flex', alignItems: 'center', gap: 7,
            fontSize: 11.5, fontWeight: 700,
          }}>
            <FaBan style={{ flexShrink: 0 }} />
            <span>{card.skipReason || `Maintenance will not be scheduled in ${season.label}.`}</span>
          </div>
        )}

        {partialApproval ? (
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {(card.scopeApprovals || []).map(({ row, inspection, status: scopeStatus }) => {
              const approved = scopeStatus !== 'pending';
              const label = row.contractType === 'dryer-vent' ? contractScopeLabel(row) : equipmentLabel(row);
              const start = parseLocalDate(inspection?.startTerm || inspection?.startDate);
              const end = parseLocalDate(inspection?.endTerm || inspection?.endDate || inspection?.startTerm);
              return (
                <div key={row.contractType} style={{
                  padding: '9px 11px', borderRadius: 8,
                  color: approved ? C.green : C.amber,
                  background: approved ? C.greenSoft : C.amberSoft,
                }}>
                  <div style={{ fontSize: 12, fontWeight: 750 }}>
                    {label} — {scopeStatus === 'completed' ? 'Completed' : approved ? 'Management approved' : 'Awaiting approval'}
                  </div>
                  <div style={{ fontSize: 11.5, marginTop: 3 }}>
                    {approved ? `${formatWindow(start, end)}${inspection?.maintenanceID ? ` · ID ${inspection.maintenanceID}` : ''}` : 'No approved dates — not booked'}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
        <div style={{
          marginTop: 10, padding: '10px 11px', borderRadius: 9,
          background: windowTone.bg,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: 10, flexWrap: 'wrap',
        }}>
          <div>
            <div style={{
              color: windowTone.fg,
              fontSize: 10.5, fontWeight: 750, textTransform: 'uppercase', letterSpacing: '.04em',
            }}>
              {windowLabel}
            </div>
            <div style={{ color: C.ink, fontSize: 16, fontWeight: 780, marginTop: 3 }}>
              {card.status === 'skipped' ? 'Not scheduled this cycle' : formatWindow(card.start, card.end)}
            </div>
            {card.status !== 'skipped' && (
              <div style={{ color: C.muted, fontSize: 11, marginTop: 3 }}>
                {workingDays || '—'} working day{workingDays === 1 ? '' : 's'}
              </div>
            )}
          </div>
          <FaCalendarAlt style={{
            color: windowTone.fg,
            fontSize: 18, opacity: .8,
          }} />
        </div>

        )}

        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
            {card.hvac && (
              <ScopeChip
                label={equipmentLabel(card.hvac)}
                sublabel={`${frequencyLabel(card.hvac)} · ${contractTermLabel(card.hvac)}`}
                tone="blue"
              />
            )}
            {card.dryer && (
              <ScopeChip
                label={contractScopeLabel(card.dryer)}
                sublabel={`${card.dryerAligned ? 'Aligned with HVAC' : frequencyLabel(card.dryer)} · ${contractTermLabel(card.dryer)}`}
                tone="amber"
              />
            )}
            {(card.skippedScopes || []).map(({ row, reason }) => (
              <span key={row.contractType} style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                color: C.muted, background: C.lineSoft, borderRadius: 7,
                padding: '6px 8px', fontSize: 11.5, lineHeight: 1.4,
              }}>
                <FaBan style={{ flexShrink: 0 }} />
                <span>
                  <strong>{row.contractType === 'dryer-vent' ? contractScopeLabel(row) : equipmentLabel(row)} — skipped {season.label}</strong>
                  {reason && <span style={{ display: 'block', marginTop: 2 }}>{reason}</span>}
                </span>
              </span>
            ))}
            {card.needsScopeReview && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                color: C.amber, background: C.amberSoft, borderRadius: 7,
                padding: '6px 8px', fontSize: 11, fontWeight: 700,
              }}>
                <FaExclamationTriangle />
                Confirm bath/kitchen exhaust scope
              </span>
            )}
            {card.extraCyclesRequired && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                color: C.red, background: C.redSoft, borderRadius: 7,
                padding: '6px 8px', fontSize: 11, fontWeight: 700,
              }}>
                <FaExclamationTriangle />
                {Math.max(...card.rows.map(cyclesPerYear))}×/year — extra non-seasonal cycle required
              </span>
            )}
            {isFinal && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                color: card.expiredContract || card.expiresBeforeVisit ? C.red : C.amber,
                background: card.expiredContract || card.expiresBeforeVisit ? C.redSoft : C.amberSoft,
                borderRadius: 7, padding: '6px 8px', fontSize: 11, fontWeight: 750,
              }}>
                <FaExclamationTriangle />
                {card.expiredContract
                  ? 'Expired — final maintenance / renewal review'
                  : card.expiresBeforeVisit
                    ? 'Contract expires before proposed visit — review final obligation'
                    : 'Contract expires after this maintenance'}
                {card.earliestExpiry ? ` (${formatDate(card.earliestExpiry)})` : ''}
              </span>
            )}
          </div>
        </div>

        <div style={{
          marginTop: 13, paddingTop: 11, borderTop: `1px solid ${C.lineSoft}`,
          display: 'flex', alignItems: 'center', gap: '8px 18px', flexWrap: 'wrap',
          color: C.muted, fontSize: 11.5,
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <FaUsers />
            Crew: <strong style={{ color: card.crew ? C.inkSoft : C.amber }}>
              {card.crew || 'Not set'}
            </strong>
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <FaFilter />
            Filters: <strong style={{ color: card.filter || !card.hvac ? C.inkSoft : C.amber }}>
              {card.filter || (card.hvac ? 'Not entered' : 'Not required')}
            </strong>
          </span>
          <span>
            {partialApproval ? 'Proposed ID' : 'ID'}: <strong style={{ color: C.inkSoft }}>{card.maintenanceId}</strong>
          </span>
        </div>
        <div style={{
          marginTop: 10, display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap',
        }}>
          {card.status === 'planning' && (
            <button
              type="button"
              onClick={onDatesSent}
              style={actionButton('primary')}
            >
              <FaClipboardCheck />
              {partialApproval ? 'Mark pending dates sent' : 'Mark dates sent'}
            </button>
          )}
          {card.status === 'sent' && (
            <button type="button" onClick={onApproveSchedule} style={actionButton('primary')}>
              <FaCheckCircle /> {partialApproval ? 'Review pending service' : 'Approve schedule'}
            </button>
          )}
          {card.status === 'scheduled' && (
            <button
              type="button"
              onClick={onUnapprove}
              title="Send this back to awaiting approval and take it off the Jobber calendar"
              style={actionButton('secondary')}
            >
              <FaUndo /> Move back
            </button>
          )}
          <button type="button" onClick={onAdjust} style={actionButton('secondary')}>
            <FaEdit /> Adjust
          </button>
          <button type="button" onClick={onHistory} style={actionButton('secondary')}>
            <FaHistory /> History
          </button>
          {card.status !== 'skipped' && !partialApproval && <button
            type="button"
            disabled={!card.start}
            title={card.start ? 'Copy a proposed-dates email' : 'Set proposed dates before copying an email'}
            onClick={async () => {
              try {
                await copyToClipboard(managementEmailCopy(card, season));
                setEmailCopyState('copied');
              } catch (error) {
                setEmailCopyState('failed');
              }
              window.setTimeout(() => setEmailCopyState('idle'), 1800);
            }}
            style={{
              ...actionButton('secondary'),
              opacity: card.start ? 1 : 0.5,
              cursor: card.start ? 'pointer' : 'not-allowed',
              color: emailCopyState === 'copied' ? C.green : emailCopyState === 'failed' ? C.red : C.inkSoft,
            }}
          >
            {emailCopyState === 'copied' ? <FaCheckCircle /> : <FaEnvelope />}
            {emailCopyState === 'copied' ? 'Copied' : emailCopyState === 'failed' ? 'Copy failed' : 'Copy email'}
          </button>}
        </div>
      </div>
    </article>
  );
}

function actionButton(tone) {
  const palette = {
    primary: { bg: C.navy, fg: '#fff', border: C.navy, cursor: 'pointer' },
    secondary: { bg: C.card, fg: C.inkSoft, border: C.line, cursor: 'pointer' },
  }[tone];
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    border: `1px solid ${palette.border}`,
    borderRadius: 7,
    background: palette.bg,
    color: palette.fg,
    padding: '7px 9px',
    fontSize: 11.5,
    fontWeight: 700,
    fontFamily: 'inherit',
    cursor: palette.cursor,
  };
}

function UnapproveModal({ card, season, onClose, onConfirm }) {
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const range = card.start
    ? `${formatDate(card.start)}${card.end ? ` – ${formatDate(card.end)}` : ''}`
    : 'No dates set';

  const confirm = async () => {
    setError('');
    setWorking(true);
    try {
      await onConfirm();
    } catch (err) {
      setError(err?.message || 'Could not move this building back.');
    } finally {
      setWorking(false);
    }
  };

  return (
    <ModalShell
      title={`Move back — ${card.name}`}
      subtitle={`${season.label} · currently management approved`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} disabled={working} style={actionButton('secondary')}>
            Cancel
          </button>
          <button type="button" onClick={confirm} disabled={working} style={actionButton('primary')}>
            {working ? 'Moving back…' : 'Move back to dates sent'}
          </button>
        </>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13, color: C.ink }}>
        <div>
          This returns <strong>{card.name}</strong> to <strong>Awaiting management approval</strong>,
          and takes its visit off the Jobber calendar so the crew is no longer booked.
        </div>
        <div style={{
          border: `1px solid ${C.line}`, borderRadius: 9, padding: '10px 11px',
          background: C.bg, color: C.inkSoft,
        }}>
          <div style={{ fontSize: 11, fontWeight: 750, color: C.muted, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4 }}>
            Approved window being released
          </div>
          <div style={{ fontWeight: 700 }}>{range}</div>
          <div style={{ marginTop: 5 }}>{approvedActionDescription(card)}</div>
          <div style={{ marginTop: 5, fontSize: 12 }}>This action applies only to this maintenance record.</div>
        </div>
        <div style={{ color: C.muted, fontSize: 12.5 }}>
          The approved dates carry over as the proposal, so you can adjust them and
          re-approve. Jobber keeps the underlying job (it has no delete), and
          re-approving reuses it rather than creating a second one.
        </div>
        <div style={{
          display: 'flex', gap: 8, alignItems: 'flex-start',
          border: `1px solid ${C.amber}33`, background: C.amberSoft,
          borderRadius: 9, padding: '10px 11px', color: C.inkSoft, fontSize: 12.5,
        }}>
          <FaExclamationTriangle style={{ color: C.amber, marginTop: 2, flexShrink: 0 }} />
          <span>
            If this cycle has already started or been worked, move it back from the
            Schedule page instead — this removes the maintenance record entirely.
          </span>
        </div>
        {error && (
          <div style={{ color: C.red, fontSize: 12.5, fontWeight: 650 }}>{error}</div>
        )}
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, subtitle, onClose, children, footer }) {
  return (
    <div className="internal-planner-modal-backdrop"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(15,23,42,.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 22,
        fontFamily: sharedFont,
      }}
    >
      <div className="internal-planner-modal" role="dialog" aria-modal="true" aria-label={title}
        onClick={(event) => event.stopPropagation()}
        style={{
          width: 'min(620px, 100%)', maxHeight: '88vh', overflow: 'hidden',
          background: C.card, borderRadius: 13, boxShadow: '0 24px 70px rgba(15,23,42,.3)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{
          padding: '16px 18px', borderBottom: `1px solid ${C.line}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
        }}>
          <div>
            <div style={{ color: C.ink, fontSize: 18, fontWeight: 800 }}>{title}</div>
            {subtitle && <div style={{ color: C.muted, fontSize: 12.5, marginTop: 3 }}>{subtitle}</div>}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{
            border: 0, background: C.lineSoft, color: C.muted, borderRadius: 7,
            width: 30, height: 30, cursor: 'pointer',
          }}>
            <FaTimes />
          </button>
        </div>
        <div style={{ overflowY: 'auto', padding: 18 }}>{children}</div>
        {footer && (
          <div style={{
            padding: '12px 18px', borderTop: `1px solid ${C.line}`,
            display: 'flex', justifyContent: 'flex-end', gap: 8,
          }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export function PlanEditModal({ card, season, onClose, onSave }) {
  const scopeRows = card.plannedRows || card.rows || [];
  const singleScope = scopeRows.length === 1;
  const [start, setStart] = useState(toIso(card.start) || '');
  const [end, setEnd] = useState(toIso(card.end) || '');
  const [crew, setCrew] = useState(card.crew || '');
  const [filterType, setFilterType] = useState(card.filterType || '');
  const [filterSize, setFilterSize] = useState(card.filterSize || '');
  const [filterQuantity, setFilterQuantity] = useState(card.filterQuantity || '');
  const [notes, setNotes] = useState(card.planningNotes || '');
  const [maintenanceId, setMaintenanceId] = useState(card.maintenanceId || nextMaintenanceId(card));
  const [dailySchedule, setDailySchedule] = useState(card.dailySchedule || '');
  const [pricingDetails, setPricingDetails] = useState(card.pricingDetails || '');
  const [jobberClient, setJobberClient] = useState({
    id: card.jobberClientId || '',
    propertyId: card.jobberPropertyId || '',
    name: card.jobberClientName || '',
  });
  const [datesSent, setDatesSent] = useState(Boolean(card.datesSent));
  const [skippedThisCycle, setSkippedThisCycle] = useState(Boolean(card.skippedThisCycle) && !singleScope);
  const [skipReason, setSkipReason] = useState(card.skipReason || '');
  const [scopeDecisions, setScopeDecisions] = useState(() => {
    const decisions = { ...(card.scopeDecisions || {}) };
    // The one visible service control also represents older whole-cycle
    // skips, so unchecking it can restore the cycle without a hidden flag.
    if (singleScope && card.skippedThisCycle) {
      decisions[scopeRows[0].contractType] = { skipped: true, reason: card.skipReason || '' };
    }
    (card.lockedContractTypes || []).forEach(type => {
      if (decisions[type]?.skipped) decisions[type] = { skipped: false, reason: '' };
    });
    return decisions;
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const showBilling = canSeeBillingDetails(readMaintenanceUser()?.role);
  const hasLockedScopes = (card.lockedContractTypes || []).length > 0;
  const allScopesSkipped = scopeRows.length > 0 && scopeRows.every(row => scopeDecisions[row.contractType]?.skipped === true);
  const visitSkipped = skippedThisCycle || allScopesSkipped;
  const changeScope = (type, patch) => {
    setScopeDecisions(current => ({
      ...current,
      [type]: { skipped: false, reason: '', ...(current[type] || {}), ...patch },
    }));
    setDatesSent(false);
  };

  const save = async () => {
    if (!visitSkipped && Boolean(start) !== Boolean(end)) {
      setError('Enter both start and end dates, or leave both blank while planning.');
      return;
    }
    if (!visitSkipped && start && end && parseLocalDate(end) < parseLocalDate(start)) {
      setError('End date cannot be before the start date.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      await onSave({
        start,
        end,
        crew: crew ? String(Math.max(1, Number(crew) || 1)) : '',
        filterType: filterType.trim(),
        filterSize: filterSize.trim(),
        filterQuantity: filterQuantity.trim(),
        notes: notes.trim(),
        maintenanceId: maintenanceId.trim(),
        dailySchedule: dailySchedule.trim(),
        pricingDetails: pricingDetails.trim(),
        jobberClientId: jobberClient.id,
        jobberPropertyId: jobberClient.propertyId,
        jobberClientName: jobberClient.name,
        datesSent: visitSkipped ? false : datesSent,
        skippedThisCycle,
        skipReason: skippedThisCycle ? skipReason.trim() : '',
        scopeDecisions: Object.fromEntries(Object.entries(scopeDecisions).map(([type, decision]) => [type, {
          skipped: decision.skipped === true,
          reason: decision.skipped === true ? String(decision.reason || '').trim() : '',
        }])),
      });
    } catch (err) {
      setError(err?.message || 'Could not save this planning record.');
    } finally {
      setSaving(false);
    }
  };

  const fieldStyle = {
    width: '100%', boxSizing: 'border-box', border: `1px solid ${C.line}`,
    borderRadius: 8, padding: '9px 10px', color: C.ink, background: '#fff',
    fontSize: 13, fontFamily: 'inherit', outline: 'none',
  };
  const labelStyle = {
    display: 'block', color: C.muted, fontSize: 11, fontWeight: 750,
    textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 5,
  };

  return (
    <ModalShell
      title={`Adjust plan — ${card.name}`}
      subtitle={`${season.label} · ${card.units ? `${card.units.toLocaleString()} units` : 'Unit count unavailable'}`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} disabled={saving} style={actionButton('secondary')}>Cancel</button>
          <button type="button" onClick={save} disabled={saving} style={actionButton('primary')}>
            {saving ? 'Saving…' : 'Save plan'}
          </button>
        </>
      )}
    >
      <div style={{
        background: C.purpleSoft, color: C.purple, borderRadius: 8,
        padding: '9px 11px', fontSize: 12, lineHeight: 1.4, marginBottom: 14,
      }}>
        {card.status === 'scheduled'
          ? <>Schedule changes apply to <strong>{approvedActionDescription(card)}</strong>. Other separately booked services retain their schedules.</>
          : <>Changes are saved to the shared production planner for the scheduling team. No maintenance inspection or Jobber visit is created until Management Approval.</>}
      </div>
      {scopeRows.length > 0 && (
        <fieldset style={{ margin: '0 0 14px', padding: '11px 12px', border: `1px solid ${C.line}`, borderRadius: 8 }}>
          <legend style={{ color: C.inkSoft, fontSize: 12.5, fontWeight: 750, padding: '0 5px' }}>
            Services for {season.label}
          </legend>
          <div style={{ color: C.muted, fontSize: 12, lineHeight: 1.5, marginBottom: 10 }}>
            {singleScope
              ? 'Skip this service for this season without changing its contract or future cycles.'
              : 'Included services are planned together. Skip an individual service for this season without changing its contract or future cycles.'}
          </div>
          {scopeRows.map(row => {
            const type = row.contractType;
            const label = type === 'dryer-vent' ? contractScopeLabel(row) : equipmentLabel(row);
            const locked = (card.lockedContractTypes || []).includes(type);
            const skipped = scopeDecisions[type]?.skipped === true;
            return (
              <div key={type} style={{ padding: '8px 0', borderTop: `1px solid ${C.lineSoft}` }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 9, color: C.inkSoft, fontSize: 12.5, fontWeight: 650 }}>
                  <input
                    type="checkbox"
                    checked={skipped}
                    disabled={locked || skippedThisCycle}
                    onChange={event => changeScope(type, { skipped: event.target.checked })}
                  />
                  Skip {label} this season
                </label>
                {locked && <div style={{ marginTop: 5, color: C.muted, fontSize: 11.5, lineHeight: 1.4 }}>
                  Already booked or completed. Change an upcoming booking through the schedule before skipping it; completed history is preserved.
                </div>}
                {skipped && !skippedThisCycle && <label style={{ display: 'block', marginTop: 8 }}>
                  <span style={labelStyle}>Reason for skipping {label}</span>
                  <textarea
                    rows={2}
                    maxLength={1000}
                    value={scopeDecisions[type]?.reason || ''}
                    onChange={event => changeScope(type, { reason: event.target.value })}
                    placeholder="e.g. Management requested HVAC only this season"
                    style={{ ...fieldStyle, resize: 'vertical' }}
                  />
                </label>}
              </div>
            );
          })}
        </fieldset>
      )}
      {!hasLockedScopes && !singleScope && (
        <div style={{
          marginBottom: 14, padding: '11px 12px',
          border: `1px solid ${skippedThisCycle ? C.muted : C.line}`,
          borderRadius: 8, background: skippedThisCycle ? C.lineSoft : '#fff',
        }}>
          <label style={{
            display: 'flex', alignItems: 'center', gap: 9,
            color: C.inkSoft, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
          }}>
            <input
              type="checkbox"
              checked={skippedThisCycle}
              onChange={(event) => {
                setSkippedThisCycle(event.target.checked);
                if (event.target.checked) setDatesSent(false);
              }}
            />
            Skip maintenance for {season.label}
          </label>
          {skippedThisCycle && (
            <label style={{ display: 'block', marginTop: 10 }}>
              <span style={labelStyle}>Reason for skipping</span>
              <textarea
                rows={2}
                value={skipReason}
                onChange={(event) => setSkipReason(event.target.value)}
                placeholder="e.g. Management requested Fall instead"
                style={{ ...fieldStyle, resize: 'vertical' }}
              />
            </label>
          )}
        </div>
      )}
      <div className="internal-form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 12 }}>
        <label>
          <span style={labelStyle}>Start date</span>
          <input type="date" value={start} onChange={(event) => setStart(event.target.value)} style={fieldStyle} />
        </label>
        <label>
          <span style={labelStyle}>End date</span>
          <input type="date" value={end} onChange={(event) => setEnd(event.target.value)} style={fieldStyle} />
        </label>
      </div>
      <div className="internal-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 13 }}>
        <label>
          <span style={labelStyle}>Maintenance ID</span>
          <input type="text" value={maintenanceId} onChange={(event) => setMaintenanceId(event.target.value)} style={{ ...fieldStyle, fontWeight: 750 }} />
        </label>
        <label>
          <span style={labelStyle}>Crew required</span>
          <input
            type="number"
            min="1"
            step="1"
            value={crew}
            onChange={(event) => setCrew(event.target.value)}
            placeholder="e.g. 4"
            style={fieldStyle}
          />
        </label>
      </div>
      <div style={{ marginTop: 13 }}>
        <span style={labelStyle}>Filter info</span>
        <div className="internal-form-grid" style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr .8fr', gap: 8 }}>
          {[
            ['Type', filterType, setFilterType, 'e.g. MERV 11'],
            ['Size', filterSize, setFilterSize, 'e.g. 16x25x1'],
            ['Per unit', filterQuantity, setFilterQuantity, 'e.g. 2'],
          ].map(([label, value, setter, placeholder]) => (
            <label key={label}>
              <span style={{ ...labelStyle, fontSize: 9.5 }}>{label}</span>
              <input type="text" value={value} onChange={(event) => setter(event.target.value)} placeholder={placeholder} style={fieldStyle} />
            </label>
          ))}
        </div>
      </div>
      <label style={{ display: 'block', marginTop: 13 }}>
        <span style={labelStyle}>Daily schedule</span>
        <textarea
          rows={3}
          value={dailySchedule}
          onChange={(event) => setDailySchedule(event.target.value)}
          placeholder="e.g. Day 1: Floors 1–5, Day 2: Floors 6–10…"
          style={{ ...fieldStyle, resize: 'vertical', fontFamily: 'monospace' }}
        />
      </label>
      <label style={{ display: 'block', marginTop: 13 }}>
        <span style={labelStyle}>Notes</span>
        <textarea
          rows={4}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="Building contact timing, crew considerations, access restrictions…"
          style={{ ...fieldStyle, resize: 'vertical' }}
        />
      </label>
      {card.status !== 'scheduled' && !visitSkipped && (
        <label style={{
          marginTop: 13, padding: '10px 11px', border: `1px solid ${C.line}`,
          borderRadius: 8, display: 'flex', alignItems: 'center', gap: 9,
          color: C.inkSoft, fontSize: 12.5, fontWeight: 650, cursor: 'pointer',
        }}>
          <input
            type="checkbox"
            checked={datesSent}
            onChange={(event) => setDatesSent(event.target.checked)}
          />
          Dates have been sent to the building
        </label>
      )}
      {showBilling && (
        <div style={{
          marginTop: 13, background: '#f0f9ff', border: '1px solid #bae6fd',
          borderRadius: 8, padding: '11px 12px',
        }}>
          <div style={{ ...labelStyle, color: '#075985' }}>Billing &amp; Jobber</div>
          <div style={{ marginBottom: 10 }}>
            <span style={{ ...labelStyle, color: '#0369a1', fontSize: 9.5 }}>Jobber client (this building)</span>
            <JobberClientPicker
              clientName={jobberClient.name}
              onPick={(client) => setJobberClient({
                id: client.id,
                propertyId: client.propertyId || '',
                name: client.name || '',
              })}
              onClear={() => setJobberClient({ id: '', propertyId: '', name: '' })}
            />
          </div>
          <label>
            <span style={{ ...labelStyle, color: '#0369a1', fontSize: 9.5 }}>Pricing details</span>
            <textarea
              rows={2}
              value={pricingDetails}
              onChange={(event) => setPricingDetails(event.target.value)}
              placeholder="Plain text — e.g. Flat $4,500 / cycle or $12/unit."
              style={{ ...fieldStyle, resize: 'vertical' }}
            />
          </label>
        </div>
      )}
      {card.notes && (
        <div style={{ marginTop: 13, background: C.lineSoft, borderRadius: 8, padding: '10px 11px' }}>
          <div style={labelStyle}>Existing contract notes</div>
          <div style={{ color: C.inkSoft, fontSize: 12.5, lineHeight: 1.45 }}>{card.notes}</div>
        </div>
      )}
      {error && <div style={{ color: C.red, fontSize: 12, fontWeight: 650, marginTop: 10 }}>{error}</div>}
    </ModalShell>
  );
}

function HistoryModal({ card, onClose }) {
  return (
    <ModalShell
      title={`Maintenance history — ${card.name}`}
      subtitle={`${card.history.length} recorded cycle${card.history.length === 1 ? '' : 's'}`}
      onClose={onClose}
    >
      {card.history.length === 0 ? (
        <div style={{ color: C.muted, textAlign: 'center', padding: '28px 12px' }}>
          No maintenance history is recorded for this contract.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {card.history.map((item, index) => {
            const start = parseLocalDate(item?.startTerm || item?.startDate);
            const end = parseLocalDate(item?.endTerm || item?.endDate || item?.startTerm);
            const type = contractTypeOfHistory(item);
            return (
              <div key={item?.objectId || item?.maintenanceID || index} style={{
                border: `1px solid ${C.line}`, borderRadius: 9, padding: '10px 11px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
              }}>
                <div>
                  <div style={{ color: C.ink, fontSize: 13, fontWeight: 750 }}>
                    {item?.cycle || (() => {
                      const occ = planningSeasonForDate(start, end);
                      return occ ? `${occ.name} ${occ.year}` : 'Maintenance';
                    })()}
                  </div>
                  <div style={{ color: C.muted, fontSize: 11.5, marginTop: 3 }}>
                    {formatWindow(start, end)}
                    {item?.maintenanceID ? ` · ${item.maintenanceID}` : ''}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{
                    display: 'inline-block', borderRadius: 999, padding: '3px 7px',
                    background: type === 'dryer-vent' ? C.amberSoft : type === 'combined' ? '#ecfdf5' : C.blueSoft,
                    color: type === 'dryer-vent' ? C.amber : type === 'combined' ? C.green : C.blue,
                    fontSize: 10.5, fontWeight: 750,
                  }}>
                    {type === 'dryer-vent' ? 'Dryer vent' : type === 'combined' ? 'HVAC + dryer' : 'HVAC'}
                  </span>
                  <div style={{ color: C.muted, fontSize: 10.5, marginTop: 4 }}>{item?.status || '—'}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </ModalShell>
  );
}

function ScopeChip({ label, sublabel, tone }) {
  const palette = tone === 'amber'
    ? { bg: C.amberSoft, fg: C.amber }
    : { bg: C.blueSoft, fg: '#1e4eaa' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: palette.bg, color: palette.fg, borderRadius: 7,
      padding: '6px 8px', fontSize: 11.5, fontWeight: 750,
    }}>
      {label}
      <span style={{ fontWeight: 550, opacity: .78 }}>· {sublabel}</span>
    </span>
  );
}

function LoadingState() {
  return (
    <div style={{
      marginTop: 18, minHeight: 280, borderRadius: 12, background: C.card,
      border: `1px solid ${C.line}`, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', color: C.muted, gap: 12,
    }}>
      <FaSyncAlt style={{ fontSize: 25, color: C.navy, animation: 'seasonal-spin 1s linear infinite' }} />
      <div style={{ fontSize: 13.5, fontWeight: 650 }}>Building the seasonal plan…</div>
    </div>
  );
}

function EmptyState({ hasFilters }) {
  return (
    <div style={{
      marginTop: 18, minHeight: 230, borderRadius: 12, background: C.card,
      border: `1px dashed ${C.line}`, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', color: C.muted, gap: 9,
      textAlign: 'center', padding: 24,
    }}>
      <FaLeaf style={{ fontSize: 24, color: C.green }} />
      <strong style={{ color: C.inkSoft }}>
        {hasFilters ? 'No buildings match these filters.' : 'No contracts fall in this seasonal block.'}
      </strong>
      <span style={{ fontSize: 12.5 }}>
        {hasFilters ? 'Clear the search or choose a different status.' : 'Try another Spring or Fall block.'}
      </span>
    </div>
  );
}
